import React, { useState, useRef, useEffect } from 'react';
// --- SES SERVİSİ ---
// 🚀 [CACHE BUSTER v2.4 - STABİLİTE GÜNCELLEMESİ]
import { audioManager } from '../services/AudioService';
// console.warn("🔥 [RecipeModal v2.4] STABİL AKIŞ AKTİF!");
import TermsBadge from './TermsBadge';
import { API_BASE_URL } from '../config';
import Swal from 'sweetalert2';
import axios from 'axios';
import { formatQuantity, parseFraction, formatCurrency } from '../utils/formatUtils';
import { calculateRecipeCostDisplay } from '../utils/recipePricing';
import { repairMojibake, repairMojibakeList } from '../utils/textRepair';
import { trackProductEvent } from '../utils/productAnalytics';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('authToken');
  if (token) return { Authorization: `Bearer ${token}`, ...extra };
  return { ...extra };
};

const createIdempotencyKey = (prefix = 'recipe_used') => {
  try {
    if (window?.crypto?.randomUUID) {
      return `${prefix}_${window.crypto.randomUUID()}`;
    }
  } catch (_) {
    // no-op
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const processText = (text, userProfile) => {
  if (!text || typeof text !== 'string') return text;
  // Support both babyName and user profile's direct property
  const babyName = userProfile?.babyName || userProfile?.bebek_ismi || 'Bebek';
  // Robust regex for {bebek_ismi} with Turkish character support and optional underscores/spaces
  // Also support cases where there might not be curly braces but just the keyword
  return text.replace(/\{bebek[_\s]?[iı]smi\}/gi, babyName)
    .replace(/bebek_ismi/gi, babyName);
};

const resolveImageSrc = (rawUrl) => {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) return value;
  if (value.startsWith('/static/') || value.startsWith('/assets/') || value.startsWith('/images/')) return value;
  if (value.startsWith('/uploads/') || value.startsWith('/api/uploads/')) return `${API_BASE_URL}${value}`;
  if (value.startsWith('uploads/')) return `${API_BASE_URL}/${value}`;
  if (value.startsWith('/')) return value;
  return `${API_BASE_URL}/${value}`;
};

const getModeIcon = (mode) => {
  const icons = {
    'oven': 'oven_gen',
    'airfryer': 'airframer',
    'pan': 'skillet',
    'pressure_cooker': 'skillet',
    'pot': 'soup_kitchen',
    'microwave': 'microwave',
    'grill': 'outdoor_grill',
    'fryer': 'fryer',
    'common': 'restaurant'
  };
  return icons[mode] || 'restaurant';
};

const parseSubSteps = (detay) => {
  if (!detay || typeof detay !== 'string') return [detay];

  // 🎯 Gelişmiş Parçalama: 1-, 1., 1) veya madde işaretlerini yakalar
  // Sadece cümlenin EN BAŞINDA veya bir boşluktan sonra gelen sayıları yakala (3-4 parmak gibi aralıkları koru).
  const normalized = detay.replace(/(?:^|\s)(\d+)\s*[-.)]\s+/g, (match, p1) => `|||${p1}|||`);
  const subSteps = normalized.split(/\|\|\|\d+\|\|\|/).filter(s => s.trim().length > 3).map(s => s.trim());

  if (subSteps.length > 1) return subSteps;

  // Fallback: Nokta ile bölme (Eğer metin çok uzunsa ve bariz cümleler varsa)
  if (detay.includes('.') && detay.length > 80) {
    const dots = detay.split(/\.\s+(?=[A-ZÇĞİÖŞÜ])/).filter(s => s.trim().length > 10);
    if (dots.length > 1) return dots.map(s => s.endsWith('.') ? s : s + '.');
  }
  return [detay];
};

const inferBirimTipi = (unit, name) => {
  const u = (unit || '').toLowerCase().trim();
  const n = (name || '').toLowerCase().trim();
  const pieceUnits = ['adet', 'tane', 'dis', 'diş', 'parca', 'parça', 'demet', 'bag', 'bağ', 'kök', 'tup', 'tüp', 'paket', 'kase'];
  if (pieceUnits.some(pu => u === pu || u.includes(pu))) return 'piece';
  const pieceKeywords = ['sogan', 'soğan', 'patates', 'havuç', 'havuc', 'yumurta', 'biber', 'domates', 'elma', 'armut'];
  if (!u && pieceKeywords.some(pk => n.includes(pk))) return 'piece';
  const volumeUnits = ['ml', 'lt', 'su bardagi', 'cay bardagi', 'kahve fincani', 'yemek kasigi', 'tatli kasigi', 'cay kasigi', 'fincan', 'bardak', 'kasik', 'kaşık'];
  if (volumeUnits.some(vu => u.includes(vu) || u === vu)) return 'volume';
  const weightUnits = ['gr', 'gram', 'kg', 'kilogram', 'mg'];
  if (weightUnits.some(wu => u.includes(wu) || u === wu)) return 'weight';
  return 'weight';
};

const getIngredientPolicyBadge = (ingredient = {}) => {
  const ingredientType = String(ingredient?.ingredientType || ingredient?.dictionary?.ingredientType || '').toLowerCase();
  const stockTracking = String(ingredient?.stockTracking || ingredient?.dictionary?.stockTracking || '').toLowerCase();
  const isStaple = Boolean(ingredient?.isStaple || ingredient?.dictionary?.isStaple);

  if (ingredientType === 'derived') {
    return {
      text: 'TÜRETİLMİŞ',
      className: 'bg-indigo-600/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20'
    };
  }

  if (stockTracking === 'disabled' || ingredientType === 'staple' || isStaple) {
    return {
      text: 'TEMEL ÜRÜN',
      className: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20'
    };
  }

  return null;
};

const buildIngredientPolicyExplain = (ingredient = {}) => {
  const ingredientType = String(ingredient?.ingredientType || ingredient?.dictionary?.ingredientType || '').toLowerCase();
  const stockTracking = String(ingredient?.stockTracking || ingredient?.dictionary?.stockTracking || '').toLowerCase();
  const shoppingBehavior = String(ingredient?.shoppingBehavior || ingredient?.dictionary?.shoppingBehavior || '').toLowerCase();
  const decrementBehavior = String(ingredient?.decrementBehavior || ingredient?.dictionary?.decrementBehavior || '').toLowerCase();
  const parentName = String(ingredient?.derivedFromName || '').trim();

  if (ingredientType === 'derived') {
    if (decrementBehavior === 'exact_then_parent' && parentName) {
      return `Tarifte bu ürün kullanılır. Stokta yoksa ${parentName.toLocaleLowerCase('tr-TR')} üzerinden tamamlanabilir.`;
    }
    return 'Tarifte bu ürün kendi adıyla görünür; stok tarafında parent ürün fallback’i kullanılabilir.';
  }

  if (stockTracking === 'disabled') {
    if (shoppingBehavior === 'never') {
      return 'Temel mutfak ürünü olarak kabul edilir. Fiyata dahildir ama stoktan düşülmez ve alışverişe yazılmaz.';
    }
    if (shoppingBehavior === 'optional') {
      return 'Temel mutfak ürünü olarak kabul edilir. Fiyata dahildir ama stoktan düşülmez; gerekirse alışverişte opsiyonel gösterilir.';
    }
    return 'Temel mutfak ürünü olarak kabul edilir. Fiyata dahildir ama stoktan düşülmez.';
  }

  return '';
};

const RecipeModal = ({
  isOpen,
  onClose,
  recipe,
  isDarkMode,
  userProfile = null,
  userPortion = null,
  selectedNarration = 'flow',
  skillLevel = 'Deneyimli',
  availableComponents = [],
  hideBabyInfo = false,
  onSelect = null,
  isSelected = false,
  onAddToPlan = null,
  selectingFor = null,
  onMarkAsUsed: onMarkAsUsedProp = null,
  onUsedSuccess = null,
  ingredientReservations = [],
  overrideInfo = null,
  onPairingClick = null,
  contextualPrompt = null,
  contextualPromptAnsweredValue = null,
  onContextualPromptAnswer = null
}) => {
  const [activeTimers, setActiveTimers] = useState({});
  const [extraLegumeStep, setExtraLegumeStep] = useState(null);
  const [legumeDetection, setLegumeDetection] = useState(null); // { name: 'Kuru Fasulye', originalKey: 'fasulye' }
  const [activeSkillLevel, setActiveSkillLevel] = useState(skillLevel || userProfile?.skillLevel || 'Acemi');
  const [markingAsUsed, setMarkingAsUsed] = useState(false);
  const [addingToShoppingList, setAddingToShoppingList] = useState(false);
  const [revealedSteps, setRevealedSteps] = useState(new Set());
  const [legumeProcessed, setLegumeProcessed] = useState(false);
  const [activeMode, setActiveMode] = useState('common');
  const [trVoice, setTrVoice] = useState(null);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [issueCategory, setIssueCategory] = useState('ingredient_issue');
  const [issueSubType, setIssueSubType] = useState('');
  const [issueIngredientName, setIssueIngredientName] = useState('');
  const [issueNote, setIssueNote] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [ingredientPresentationMode, setIngredientPresentationMode] = useState('traditional');
  const hasBabySignal = Boolean(
    userProfile?.hasBaby ||
    userProfile?.bebek_var_mi ||
    userProfile?.babyAgeGroup ||
    userProfile?.bebek_yas_grubu ||
    userProfile?.babyBirthDate ||
    userProfile?.babyName
  );

  // [Contract] Derive matchPerc from backend facts (Single Source of Truth)
  const resolvedMatch = recipe?.match || {};
  const matchPerc = resolvedMatch.matchPercentage ?? recipe?.matchPercentage ?? null;
  const cookabilityScore = recipe?.cookabilityScore ?? matchPerc;
  const insightData = recipe?.insight || null;
  const sktInsight = insightData?.skt || null;
  const rescuedCount = Number(sktInsight?.rescuedCount || 0);
  const urgencyScore = Number(sktInsight?.urgencyScore || 0);
  const hasCriticalRescueSignal = rescuedCount > 0 && urgencyScore > 0;
  const safetyStatus = recipe?.safetyStatus || 'SAFE';
  const aceVariations = recipe?.aceVariations || [];
  const isAdapted = safetyStatus === 'ADAPTED' || !!resolvedMatch?.isAdapted || !!recipe?.isAdapted;
  const issueIngredients = (Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0
    ? recipe.scaledIngredients
    : Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0
    ? recipe.ingredients
    : Array.isArray(recipe?.malzemeler)
    ? recipe.malzemeler
    : [])
    .map((ing, idx) => {
      if (typeof ing === 'string') {
        return {
          id: `str_${idx}`,
          name: ing.trim(),
          raw: ing
        };
      }
      const name = String(
        ing?.name ||
          ing?.malzeme ||
          ing?.urun_adi ||
          ing?.displayName ||
          ing?.display_name ||
          ''
      ).trim();
      if (!name) return null;
      return {
        id: ing?.dictionaryId || ing?.ingredientId || ing?.id || `obj_${idx}`,
        name,
        raw: ing
      };
    })
    .filter(Boolean);

  const deriveSourceKitchen = () => {
    const source = String(
      recipe?.sourceKitchen ||
        recipe?.activeKitchen ||
        recipe?.kitchen ||
        recipe?.source ||
        ''
    )
      .trim()
      .toLowerCase();

    const allowed = new Set(['smart_search', 'diet', 'athlete', 'allergen', 'special_days', 'all_recipes']);
    return allowed.has(source) ? source : 'all_recipes';
  };

  const getFavoriteEntityId = () => {
    const rawId = recipe?.idRaw || recipe?.id || null;
    if (rawId !== null && rawId !== undefined && String(rawId).trim()) {
      return String(rawId);
    }
    const name = String(recipe?.name || recipe?.title || '').trim().toLowerCase();
    if (!name) return null;
    const key = name.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 120);
    return key ? `name:${key}` : null;
  };

  useEffect(() => {
    let mounted = true;
    const loadFavorite = async () => {
      if (!isOpen) return;
      const entityId = getFavoriteEntityId();
      if (!entityId) return;
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          if (mounted) setIsFavorite(false);
          return;
        }
        const qs = new URLSearchParams({
          entityType: 'recipe',
          entityId,
          type: 'liked',
          limit: '1'
        });
        const resp = await fetch(`${API_BASE_URL}/api/favorites?${qs.toString()}`, {
          headers: getAuthHeaders()
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        if (mounted) setIsFavorite(items.length > 0);
      } catch (_) {
        // silent fallback
      }
    };
    loadFavorite();
    return () => {
      mounted = false;
    };
  }, [isOpen, recipe?.id, recipe?.idRaw, recipe?.name, recipe?.title]);

  useEffect(() => {
    if (!isOpen) return;
    setIngredientPresentationMode('traditional');
  }, [isOpen, recipe?.id]);

  const handleToggleFavorite = async () => {
    if (favoriteBusy) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      Swal.fire({
        icon: 'info',
        title: 'Giris gerekli',
        text: 'Favorilere eklemek icin once giris yapmalisin.'
      });
      return;
    }

    const entityId = getFavoriteEntityId();
    if (!entityId) {
      Swal.fire({
        icon: 'error',
        title: 'Tarif kimligi bulunamadi',
        text: 'Bu tarif simdilik favorilere eklenemiyor.'
      });
      return;
    }

    setFavoriteBusy(true);
    try {
      if (isFavorite) {
        const resp = await fetch(`${API_BASE_URL}/api/favorites`, {
          method: 'DELETE',
          headers: {
            ...getAuthHeaders({ 'Content-Type': 'application/json' })
          },
          body: JSON.stringify({
            entityType: 'recipe',
            entityId,
            type: 'liked'
          })
        });
        if (!resp.ok) throw new Error('remove_failed');
        setIsFavorite(false);
      } else {
        const resp = await fetch(`${API_BASE_URL}/api/favorites`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders({ 'Content-Type': 'application/json' })
          },
          body: JSON.stringify({
            entityType: 'recipe',
            entityId,
            type: 'liked',
            metadata: {
              recipeName: recipe?.name || recipe?.title || null,
              imageUrl: recipe?.imageUrl || recipe?.image_url || null
            }
          })
        });
        if (!resp.ok) throw new Error('add_failed');
        setIsFavorite(true);
      }
    } catch (_) {
      Swal.fire({
        icon: 'error',
        title: 'Favori islemi basarisiz',
        text: 'Lutfen tekrar dene.'
      });
    } finally {
      setFavoriteBusy(false);
    }
  };

  const buildIssueSnapshot = () => {
    const sourceList = issueIngredients;
    const normalizedIngredients = sourceList.map((item) => {
      const raw = item.raw || {};
      const qtyRaw = Number(
        raw?.rawAmount ?? raw?.amountRaw ?? raw?.quantity ?? raw?.miktar ?? raw?.amount ?? 1
      );
      const rawAmount = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
      const rawUnit = String(raw?.rawUnit || raw?.unitRaw || raw?.unit || raw?.birim || 'adet').trim() || 'adet';
      const displayText =
        String(raw?.displayText || '').trim() || `${rawAmount} ${rawUnit} ${item.name}`.trim();
      const humanizeSource = String(raw?.humanizeSource || raw?.source || 'ui_snapshot').trim() || 'ui_snapshot';
      const humanizeConfidence = String(raw?.humanizeConfidence || 'medium').trim() || 'medium';

      return {
        ingredientId: raw?.dictionaryId || raw?.ingredientId || raw?.id || null,
        name: item.name,
        rawAmount,
        rawUnit,
        displayText,
        humanizeSource,
        humanizeConfidence
      };
    });

    const totalPrice = Number(
      recipe?.pricing?.totalPrice ??
        recipe?.priceSnapshot?.totalPrice ??
        recipe?.price?.total ??
        recipe?.cost?.total ??
        0
    );

    return {
      portion: Number(userPortion || userProfile?.serving_count || userProfile?.kisi_sayisi || 2),
      engineVersion: String(recipe?.engineVersion || 'unknown'),
      humanizeRule: String(recipe?.humanizeRule || 'default'),
      ingredients: normalizedIngredients,
      totalPrice: Number.isFinite(totalPrice) ? totalPrice : 0,
      sourceKitchen: deriveSourceKitchen()
    };
  };

  const wakeLockRef = useRef(null);

  /**
   * 🧠 Intelligence Event Sender
   */
  const sendIntelligenceEvent = async (eventType, extraPayload = {}) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const portions = userPortion || userProfile?.serving_count || userProfile?.kisi_sayisi || 2;
      await axios.post(`${API_BASE_URL}/api/intelligence/event`, {
        type: eventType,
        payload: {
          recipeId: recipe.id,
          recipeData: recipe,
          portions: portions,
          ...extraPayload
        }
      }, {
        headers: {
          ...getAuthHeaders(),
          Authorization: `Bearer ${token}`
        }
      });
    } catch (e) {
      console.warn(`[Intelligence] Event failed: ${String(eventType || 'unknown')}`, e.message);
    }
  };

  // --- INTELLIGENCE HEARTBEAT ---
  useEffect(() => {
    if (!isOpen || !recipe) return;

    // Initial signal: Recipe is being viewed
    sendIntelligenceEvent('RECIPE_SELECTED');
    trackProductEvent('RECIPE_VIEWED', {
      source: 'recipe_modal',
      surface: 'recipe_modal',
      context: 'manual_open',
      metadata: {
        recipeId: recipe.id,
        recipeName: recipe.name || recipe.title || null
      }
    });

    // Periodic Heartbeat (Every 3 minutes while modal is open)
    const interval = setInterval(() => {
      sendIntelligenceEvent('HEARTBEAT');
    }, 3 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isOpen, recipe?.id]);

  // --- SESLERİ ÖNCEDEN YÜKLE (VOICE PRE-LOADING) ---
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find(v => v.lang.toLowerCase().includes('tr'));
      if (voice) {
        console.log("🗣️ [RecipeModal] Türkçe ses bulundu:", voice.name);
        setTrVoice(voice);
      }
    };
    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);


  // WakeLock: Modal açıkken ekranın kapanmasını engeller
  useEffect(() => {
    if (!isOpen) return;
    const requestWakeLock = async () => {
      if ('wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch (err) {
          console.error(`❌ WakeLock Hatası: ${err.name}`);
        }
      }
    };
    requestWakeLock();
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, [isOpen]);

  // Arka plan scroll'unu engelle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 🔥 [DEBUG LOG] Agresif Veri Takibi
  useEffect(() => {
    if (isOpen && recipe) {
      console.group("🚀 [RecipeModal DEBUG]");
      console.log("Recipe ID:", recipe.id);
      console.log("Recipe Name:", recipe.name || recipe.title);
      console.log("Available Keys:", Object.keys(recipe));
      console.log("Image URL:", recipe.image_url || recipe.imageUrl);
      console.log("Steps Info:", {
        flow: recipe.steps?.flow?.length || 0,
        original: recipe.steps?._original?.length || 0,
        instructions: Array.isArray(recipe.instructions) ? recipe.instructions.length : (typeof recipe.instructions === 'string' ? "String" : "Empty")
      });
      console.log("Ingredients Length:", Array.isArray(recipe.malzemeler) ? recipe.malzemeler.length : "Not an array");
      console.log("API_BASE_URL:", API_BASE_URL);
      console.log("Full Recipe Object:", recipe);
      console.groupEnd();
    }
  }, [isOpen, recipe]);

  // sync internal state with external prop ONLY when modal opens
  useEffect(() => {
    if (isOpen) {
      // Priority: Prop > userProfile > default 'acemi'
      const rawLevel = (skillLevel || userProfile?.skillLevel || 'acemi').toString().toLowerCase();

      if (rawLevel.includes('sef') || rawLevel.includes('şef')) {
        setActiveSkillLevel('sef');
      } else if (rawLevel.includes('usta') || rawLevel.includes('deneyimli') || rawLevel.includes('orta')) {
        setActiveSkillLevel('usta');
      } else {
        setActiveSkillLevel('acemi');
      }

      setExtraLegumeStep(null);
      setLegumeProcessed(false);
      setLegumeDetection(null);
    }
  }, [isOpen, skillLevel, userProfile?.skillLevel]);

  // Auto-select first available mode if common is not the only one
  useEffect(() => {
    if (isOpen && recipe?.cookingModes) {
      const modes = Object.entries(recipe.cookingModes)
        .filter(([_, cfg]) => {
          if (!cfg || typeof cfg !== 'object') return false;
          const hasTime = cfg.time !== undefined && cfg.time !== null && String(cfg.time).trim() !== '';
          const hasTemp = cfg.temp !== undefined && cfg.temp !== null && String(cfg.temp).trim() !== '';
          const hasLabel = cfg.label !== undefined && cfg.label !== null && String(cfg.label).trim() !== '';
          return hasTime || hasTemp || hasLabel;
        })
        .map(([mode]) => mode);

      if (modes.length > 0 && !modes.includes(activeMode)) {
        // If 'common' is not the only mode, or if unique modes exist, switch to the first significant one
        // Priority: pot > oven > pan > pressure_cooker > airfryer
        const priority = ['pot', 'oven', 'pan', 'pressure_cooker', 'airfryer'];
        const bestMode = priority.find(m => modes.includes(m)) || modes[0];

        console.log(`🔄 [Mode Auto-Switch] Switching from '${activeMode}' to '${bestMode}' (Available: ${modes.join(', ')})`);
        setActiveMode(bestMode);
      }
    }
  }, [isOpen, recipe?.id, activeMode, recipe?.cookingModes]); // checking recipe.id is safer

  // --- LEGUME DETECTION (DictionaryID-based with fallback) ---
  useEffect(() => {
    if (!isOpen || !recipe) return;

    // RULE ID-01: Ingredient matching MUST use DictionaryID
    const LEGUME_DICTIONARY_IDS = [
      'cmltr5e1a004mlvscst8sy9mb', // Barbunya
      'cmltr5e10004klvscyir8qgcd', // Yeşil Mercimek
      'cmltr5e15004llvsc40ezbwip', // Kırmızı Mercimek
      'cmltr5e0v004jlvsceay1gmzd', // Nohut
      'cmltr5e0p004ilvscu2xbf6jv', // Kuru Fasulye
      'cmltr5e1g004nlvscy6p5b5bp'  // Börülce
    ];

    const LEGUME_NAME_FALLBACKS = ['nohut', 'fasulye', 'bezelye', 'börülce', 'bakla', 'barbunya', 'mercimek'];

    const ingredients = recipe.malzemeler || recipe.ingredients || [];

    console.log('🔍 [LEGUME DEBUG]', {
      recipeName: recipe.name || recipe.title,
      ingredientCount: ingredients.length,
      sampleIngredient: ingredients[0]
    });

    const detectedLegume = ingredients.find(ing => {
      const dictId = ing.ingredient?.dictionaryId || ing.dictionaryId;
      const dictCategory = ing.ingredient?.category;
      const name = (ing.ingredient?.name || ing.name || ing.malzeme || '').toLowerCase();

      const matchedByDictId = dictId && LEGUME_DICTIONARY_IDS.includes(dictId);
      const isBakliyatCategory = dictCategory === 'bakliyat';

      // 🔥 [BUGFIX] "Taze Fasulye" (Sebze) bakliyat modalını tetiklememeli. 
      // SADECE kuru veya belirtilmemiş fasulye durumlarında çalışmalı.
      const isTaze = name.includes('taze');
      const matchedByName = !isTaze && LEGUME_NAME_FALLBACKS.some(fallback => name.includes(fallback));

      if (matchedByDictId || isBakliyatCategory || matchedByName) {
        console.log('✅ [LEGUME FOUND]', {
          method: matchedByDictId ? 'DictionaryID' : 'Name Fallback',
          dictId,
          name,
          ingredient: ing
        });
      }

      return matchedByDictId || matchedByName;
    });

    if (detectedLegume && !legumeProcessed) {
      const legumeName = typeof detectedLegume === 'string'
        ? detectedLegume
        : (detectedLegume.ingredient?.name || detectedLegume.name || detectedLegume.malzeme || 'Bakliyat');

      console.log('🥘 [LEGUME MODAL TRIGGERED]', { legumeName });

      setLegumeDetection({
        name: legumeName,
        originalKey: legumeName.toLowerCase()
      });
    } else {
      console.log('❌ [NO LEGUME DETECTED]');
    }
  }, [isOpen, recipe, legumeProcessed]);

  // --- DONDURULMUŞ ÜRÜN KONTROLÜ (FREEZER CHECK) ---
  useEffect(() => {
    if (!isOpen || !recipe) return;

    const checkFrozenItems = () => {
      if (recipe.frozenIngredients && recipe.frozenIngredients.length > 0) {
        const items = recipe.frozenIngredients.join(', ');
        Swal.fire({
          title: 'Dondurucu Kontrolü ❄️',
          text: `Şefim; bu tarifteki ${items} buzlukta görünüyor. Bu tarz ürünlerin 8 saat kadar çözdürülmesi gerekir. Ürün hala buzlukta mı?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Evet, Hala Buzlukta',
          cancelButtonText: 'Hayır, Çözdürdüm / Taze',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#10b981'
        }).then((result) => {
          if (result.isConfirmed) {
            Swal.fire({
              title: 'Fikir Değişikliği? 🤔',
              text: 'Şefim bu ürünü çözdürmeniz gerekiyordu. İsterseniz bunu buzdolabına alın, bugün başka bir yemek yapalım.',
              icon: 'info',
              confirmButtonText: 'Başka Tarif Bakalım',
              showCancelButton: true,
              cancelButtonText: 'Yine de Devam Et'
            }).then((switchResult) => {
              if (switchResult.isConfirmed) {
                onClose(); // Modalı kapat ve listeye dön
              }
            });
          } else {
            Swal.fire({
              title: 'Harika! 👨‍🍳',
              text: 'Hazırlıklar tamamlanmış, o zaman tarife geçelim.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          }
        });
      }
    };

    // Modal animasyonu bitince çalışsınlar
    const timer = setTimeout(() => {
      // checkLegumes(); // DISABLED: Handled via in-UI state 'legumeDetection'
      checkFrozenItems();
    }, 500);

    return () => clearTimeout(timer);

  }, [isOpen, recipe?.id]); // recipe.id değişirse tekrar çalışsın

  const openIssueModal = () => {
    setIssueCategory('ingredient_issue');
    setIssueSubType('');
    setIssueIngredientName(issueIngredients[0]?.name || '');
    setIssueNote('');
    setIssueModalOpen(true);
  };

  const handleSubmitRecipeIssue = async () => {
    try {
      if (issueCategory === 'ingredient_issue' && !String(issueIngredientName || '').trim()) {
        Swal.fire('Uyarı', 'Lutfen ilgili malzemeyi secin.', 'warning');
        return;
      }
      if (issueCategory === 'taste_issue' && !String(issueSubType || '').trim()) {
        Swal.fire('Uyarı', 'Lutfen lezzet alt tipini secin.', 'warning');
        return;
      }

      setIssueSubmitting(true);
      const payload = {
        recipeId: String(recipe?.id || ''),
        category: issueCategory,
        issueSubType: issueCategory === 'taste_issue' ? issueSubType : null,
        ingredientRef:
          issueCategory === 'ingredient_issue'
            ? {
                ingredientId:
                  issueIngredients.find((item) => item.name === issueIngredientName)?.id || null,
                ingredientName: String(issueIngredientName || '').trim()
              }
            : null,
        note: String(issueNote || '').trim() || null,
        sourceKitchen: deriveSourceKitchen(),
        userPortion: Number(userPortion || userProfile?.serving_count || userProfile?.kisi_sayisi || 2),
        snapshot: buildIssueSnapshot()
      };

      const res = await fetch(`${API_BASE_URL}/api/recipe-issues`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || 'Bildirimi gonderemedik');
      }

      setIssueModalOpen(false);
      Swal.fire('Tesekkurler', 'Bildirimin alindi.', 'success');
    } catch (err) {
      console.error('recipe issue submit failed', err);
      Swal.fire('Hata', err?.message || 'Bildirimi gonderemedik, lutfen tekrar dene.', 'error');
    } finally {
      setIssueSubmitting(false);
    }
  };

  if (!isOpen || !recipe) return null;

  // Helper to process text based on user profile AND active skill level
  const processSkillLevelText = (textObj, profile) => {
    if (typeof textObj === 'string') return processText(textObj, profile);

    // Dynamic skill level selection
    // Normalize to: 'acemi', 'usta', 'sef'
    let level = (activeSkillLevel || profile?.skillLevel || 'acemi').toLowerCase();

    // QuickSearch might pass 'Usta Şef' or 'Deneyimli' - mapping ensure consistency
    if (level.includes('sef') || level.includes('şef')) level = 'sef';
    else if (level.includes('usta') || level.includes('orta') || level.includes('deneyimli')) level = 'usta';
    else level = 'acemi';

    let result = '';
    // STRICT HIERARCHY
    if (level === 'sef') {
      result = textObj.detay_sef || textObj.detay_usta || textObj.detay || textObj.step || '';
    } else if (level === 'usta') {
      result = textObj.detay_usta || textObj.detay || textObj.step || '';
    } else {
      // level === 'acemi'
      // For beginners, prefer 'detay_acemi', then generic 'detay'. 
      // NEVER fallback to 'detay_usta' or 'detay_sef'
      result = textObj.detay_acemi || textObj.detay || textObj.step || '';
    }

    return processText(result, profile);
  };

  const getInstructions = () => {
    let rawInstructions = [];
    let sourceInstructions = recipe.instructions;

    if (typeof sourceInstructions === 'string') {
      try {
        if (sourceInstructions.trim().startsWith('[')) {
          sourceInstructions = JSON.parse(sourceInstructions);
        }
      } catch (e) { }
    }

    if (Array.isArray(sourceInstructions) && sourceInstructions.length > 0) {
      rawInstructions = [...sourceInstructions];
    } else if (recipe.steps && typeof recipe.steps === 'object') {
      rawInstructions = [...(recipe.steps[selectedNarration] || recipe.steps.flow || recipe.steps.orta || [])];
    }

    // LEGACY: Fallback to string split if it's not an array or JSON
    if (rawInstructions.length === 0 && typeof sourceInstructions === 'string' && sourceInstructions.trim()) {
      rawInstructions = sourceInstructions.split('\n').filter(s => s.trim()).map((s, i) => ({ detay: s.trim(), adim: i + 1 }));
    }

    // DIAMOND HARD: If we STILL have no instructions, try to fallback to any sensible array
    if (rawInstructions.length === 0) {
      rawInstructions = (recipe.malzemeler?.filter(m => typeof m === 'object' && (m.adim || m.islem)) ||
        recipe.ingredients?.filter(m => typeof m === 'object' && (m.adim || m.islem)) || []);
    }

    // Preserve the original variable if needed, but let's be clean
    let rawSteps = [...rawInstructions];

    if (extraLegumeStep) {
      // Avoid double injection if backend already added it
      const exists = rawSteps.some(s => {
        const text = (typeof s === 'object' ? (s.detay || s.step || '') : s).toLowerCase();
        return text.includes('bakliyatlar ıslatılmadığı');
      });
      if (!exists) rawSteps.unshift(extraLegumeStep);
    }

    // --- VARIETY FILTERING: Remove redundant "soak" steps if choice is made ---
    // --- FILTER: Soaking Instructions ---
    // User Requirement: "Remove preparation area" if ANY choice is made.
    if (legumeProcessed) {
      rawSteps = rawSteps.filter(s => {
        // Robust check: Search in the entire object string to catch any property (step, detay, description, etc.)
        const text = (typeof s === 'object' ? JSON.stringify(s) : s).toLowerCase();
        // Aggressive keywords for "Soaking"
        const conflictKeywords = [
          'geceden ıslat', 'suya bastır', 'akşamdan', 'ertesi gün',
          'geceden suda', 'suda beklet', 'bir gece önceden', 'ıslatılan',
          'ıslatın', 'süzüp yıkayın', 'suyunu süz' // Added more specific actions often in prep steps
        ];

        // Return TRUE to KEEP, FALSE to REMOVE
        return !conflictKeywords.some(kw => text.includes(kw));
      });
    }

    if (hideBabyInfo) {
      rawSteps = rawSteps.filter(s => {
        const type = typeof s === 'object' ? s.type : null;
        const text = (typeof s === 'object' ? JSON.stringify(s) : String(s || '')).toLowerCase();
        const babyKeywords = ['bebek', 'baby', 'porsiyon ayır', 'kenara ayır', 'portion_split'];
        return type !== 'portion_split' && !babyKeywords.some(keyword => text.includes(keyword));
      });
    }

    if (!hideBabyInfo && hasBabySignal) {
      const hasBabyStep = rawSteps.some((s) => {
        const type = typeof s === 'object' ? String(s.type || '').toLowerCase() : '';
        const text = (typeof s === 'object' ? JSON.stringify(s) : String(s || '')).toLowerCase();
        return type === 'portion_split' || text.includes('bebek') || text.includes('porsiyon ayır') || text.includes('kenara ayır');
      });
      if (!hasBabyStep) {
        const fallbackText =
          recipe?.babyAdjustment ||
          recipe?.babyInfo ||
          recipe?.babyNote ||
          'Tuz ve baharat eklemeden once bebeginiz icin bir porsiyonu ayirip kenara alin.';
        rawSteps.unshift({
          type: 'portion_split',
          detay_acemi: fallbackText,
          detay_usta: fallbackText,
          detay_sef: fallbackText
        });
      }
    }

    if (recipe.pairingStep) {
      rawSteps.unshift({ step: recipe.pairingStep, detay: recipe.pairingStep, mode: 'common', isPairing: true });
    }

    return rawSteps.map((s, idx) => ({
      ...(typeof s === 'object' ? s : { detay: s }),
      adim: idx + 1
    }));
  };

  const instructions = getInstructions();
  const validCookingModes = Object.entries(recipe?.cookingModes || {}).filter(([_, cfg]) => {
    if (!cfg || typeof cfg !== 'object') return false;
    const hasTime = cfg.time !== undefined && cfg.time !== null && String(cfg.time).trim() !== '';
    const hasTemp = cfg.temp !== undefined && cfg.temp !== null && String(cfg.temp).trim() !== '';
    const hasLabel = cfg.label !== undefined && cfg.label !== null && String(cfg.label).trim() !== '';
    return hasTime || hasTemp || hasLabel;
  });
  const validCookingModeKeys = validCookingModes.map(([mode]) => mode);

  const handleModalClose = () => {
    onClose();
  };

  const handleAddToShoppingList = async () => {
    // UI'daki eksik malzeme mantığıyla aynı - resolvedMatch.missingIngredients kullan
    const sourceIngredients = recipe.scaledIngredients && recipe.scaledIngredients.length > 0
      ? recipe.scaledIngredients
      : recipe.ingredients && recipe.ingredients.length > 0
      ? recipe.ingredients
      : (recipe.malzemeler && recipe.malzemeler.length > 0
        ? recipe.malzemeler
        : []);
    
    // Normalize function - UI ile aynı
    const normalizeLooseName = (value) => String(value || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i')
      .replace(/[^a-z0-9]/g, '');
    
    // Missing name set - UI ile aynı
    const missingNameSet = new Set(
      (resolvedMatch.missingIngredients || [])
        .map((mIng) => {
          if (typeof mIng === 'string') return mIng;
          return mIng?.name || mIng?.ingredientName || mIng?.malzeme || '';
        })
        .map((name) => normalizeLooseName(name))
        .filter(Boolean)
    );
    
    // Eksik malzemeleri bul - UI ile aynı mantık
    const missingItems = sourceIngredients
      .filter((rawIng) => {
        if (typeof rawIng !== 'object') return false;
        
        const name = (rawIng.name || rawIng.malzeme || rawIng.urun_adi || rawIng.displayName || rawIng.display_name || '').toLocaleLowerCase('tr-TR');
        const normalized_name = normalizeLooseName(name);
        
        // Eksik listesinde var mı kontrol et
        return missingNameSet.has(normalized_name) || Array.from(missingNameSet).some((m) => m && (m.includes(normalized_name) || normalized_name.includes(m)));
      });
    
    if (missingItems.length === 0) {
      Swal.fire('Bilgi', 'Eksik malzeme bulunmuyor.', 'info');
      return;
    }

    setAddingToShoppingList(true);
    try {
      const itemsToAdd = missingItems.map(rawIng => {
        const ing = scaleIngredient(rawIng);
        return {
          name: ing.ingredient?.name || ing.name || '',
          amount: ing.quantity || 1,
          unit: ing.unit || 'adet',
          note: `Tarif: ${recipe.title || recipe.name}`
        };
      });

      await axios.post(`${API_BASE_URL}/api/shopping/lists`, {
        name: `${recipe.title || recipe.name} - Eksikler`,
        items: itemsToAdd
      }, { headers: getAuthHeaders() });

      Swal.fire({
        title: 'Başarılı! 🛒',
        text: 'Eksik malzemeler alışveriş listenize eklendi.',
        icon: 'success',
        confirmButtonColor: '#10b981'
      });
    } catch (error) {
      console.error('Shopping list error:', error);
      Swal.fire('Hata', 'Alışveriş listesine eklenirken bir sorun oluştu.', 'error');
    } finally {
      setAddingToShoppingList(false);
    }
  };

  const handleMarkAsUsed = async () => {
    if (markingAsUsed) return;

    // Alakart ERP Override
    if (onMarkAsUsedProp) {
      setMarkingAsUsed(true);
      try {
        await onMarkAsUsedProp(recipe);
        onClose();
      } finally {
        setMarkingAsUsed(false);
      }
      return;
    }

    setMarkingAsUsed(true);
    try {
      // 🧠 Intelligence: This is a 100% confidence signal
      await sendIntelligenceEvent('MENU_APPROVED');
      const idempotencyKey = createIdempotencyKey(`recipe_used_${recipe?.id || 'unknown'}`);

      const response = await fetch(`${API_BASE_URL}/api/recipes/used`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKey,
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          recipeData: recipe,
          idempotencyKey,
          rating: null,
          usedPortion: userPortion || userProfile?.serving_count || userProfile?.kisi_sayisi || 1,
          cookingNotes: `Cooked for ${userPortion || userProfile?.serving_count || userProfile?.kisi_sayisi || 1} portions.`
        })
      });

      if (response.ok) {
        if (typeof onUsedSuccess === 'function') {
          try {
            onUsedSuccess(recipe);
          } catch (_) {
            // no-op
          }
        }
        // stopSpeaking(); // If you have narration
        Swal.fire({
          title: 'Afiyet Olsun! 👨‍🍳',
          text: 'Tarif başarıyla işaretlendi ve malzemeler stoktan düşüldü.',
          icon: 'success',
          confirmButtonColor: '#ff0000',
          confirmButtonText: 'Harika!'
        }).then(() => {
          onClose();
        });
      } else {
        const errorData = await response.json();
        console.error('❌ Error marking recipe as used:', errorData);
        Swal.fire({
          title: 'Hata',
          text: 'Tarif işaretlenirken bir sorun oluştu.',
          icon: 'error',
          confirmButtonColor: '#ff0000'
        });
      }
    } catch (error) {
      console.error('❌ Network error marking recipe as used:', error);
      Swal.fire({
        title: 'Bağlantı Hatası',
        text: 'Sunucuya ulaşılamadı.',
        icon: 'error',
        confirmButtonColor: '#ff0000'
      });
    } finally {
      setMarkingAsUsed(false);
    }
  };

  // 🎯 UNIFIED SCALING & DURATION LOGIC
  const currentPortion =
    Number(userPortion) ||
    Number(recipe?.targetPortion) ||
    Number(recipe?.presentation?.appliedPortion) ||
    Number(userProfile?.serving_count) ||
    Number(userProfile?.kisi_sayisi) ||
    1;
  const hasScaledIngredientsPayload = Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0;
  const isAlreadyScaled = !!(
    recipe.isPreScaled ||
    recipe.presentation?.isPreScaled ||
    recipe.presentation?.isAlreadyScaled ||
    hasScaledIngredientsPayload ||
    recipe?.priceSnapshot?.isScaled
  );
  const baseSrv = 1;
  const appliedPortion = parseInt(recipe.presentation?.appliedPortion || recipe.targetPortion)
    || (isAlreadyScaled ? currentPortion : baseSrv);
  const factualMissingCount = resolvedMatch.missingCount ?? recipe.missingCount ?? recipe.missingIngredients?.length ?? null;
  const isFactuallyFullMatch = (resolvedMatch.isFull !== undefined)
    ? !!resolvedMatch.isFull
    : (factualMissingCount === 0 && ((resolvedMatch.matchPercentage ?? recipe.matchPercentage ?? 0) >= 100));
  const activeCostBreakdown = Array.isArray(recipe?.priceSnapshot?.breakdown) && recipe.priceSnapshot.breakdown.length > 0
    ? recipe.priceSnapshot.breakdown
    : (Array.isArray(recipe?.costBreakdown) ? recipe.costBreakdown : []);
  const { displayedCostBreakdown, displayedCostTotal } = calculateRecipeCostDisplay(recipe, currentPortion);
  const isWaterLike = (name = '') => {
    const key = String(name || '').toLocaleLowerCase('tr-TR').trim();
    return key === 'su' || key.includes(' su') || key.includes('su ') || key.includes('sıcak su') || key.includes('icme suyu') || key.includes('içme suyu') || key.includes('water');
  };
  const pseudoWaterUnitCost = 0.2;
  const pseudoWaterExtraTotal = activeCostBreakdown.reduce((sum, item, idx) => {
    const isKnown = item?.priceKnown;
    if (isKnown) return sum;
    if (!isWaterLike(item?.name)) return sum;
    const baseLine = Number(displayedCostBreakdown[idx] || 0);
    // If backend already calculated a non-zero line, don't add pseudo amount again.
    if (baseLine > 0) return sum;
    return sum + pseudoWaterUnitCost;
  }, 0);
  const adjustedDisplayedCostTotal = Number(displayedCostTotal || 0) + pseudoWaterExtraTotal;
  const HARD_UNPRICED_REASONS = new Set([
    'missing_price_data',
    'missing_piece_weight',
    'invalid_price_per_gram',
    'invalid_grams',
    'cost_calculation_error'
  ]);
  const unknownNonWaterCount = activeCostBreakdown.filter(
    (item) =>
      !item?.priceKnown
      && !isWaterLike(item?.name)
      && HARD_UNPRICED_REASONS.has(String(item?.unpricedReason || ''))
  ).length;
  const anomalyExcludedCount = activeCostBreakdown.filter(
    (item) => !item?.priceKnown && String(item?.unpricedReason || '') === 'anomaly_guard'
  ).length;
  const priceStatus = String(
    recipe?.priceSnapshot?.status ||
    (unknownNonWaterCount > 0 ? 'partial' : 'known')
  ).toLowerCase();
  const normalizeCategoryKey = (value = '') => {
    const key = String(value || '')
      .toLocaleLowerCase('tr-TR')
      .replace(/[_-]+/g, ' ')
      .trim();
    if (!key) return '';
    if (key.includes('sulu')) return 'sulu_yemek';
    if (key.includes('çorba') || key.includes('corba')) return 'sulu_yemek';
    if (key.includes('tavuk')) return 'tavuk';
    if (key.includes('et') || key.includes('kebap') || key.includes('köfte') || key.includes('kofte')) return 'et';
    return key;
  };
  const categoryAdjust = {
    sulu_yemek: 2.6,
    tavuk: 3.0,
    et: 3.4
  };
  const defaultOutsideMultiplier = 3.0;
  const detectedCategory = normalizeCategoryKey(
    recipe?.outsidePriceCategory ||
    recipe?.categorySlug ||
    recipe?.category ||
    recipe?.serveStyle ||
    ''
  );
  const outsideMultiplier = categoryAdjust[detectedCategory] || defaultOutsideMultiplier;
  const outsidePrice = Number((adjustedDisplayedCostTotal * outsideMultiplier).toFixed(2));
  const saving = Number((outsidePrice - adjustedDisplayedCostTotal).toFixed(2));
  const savingTone = saving > 0 ? 'positive' : (saving < 0 ? 'warning' : 'neutral');
  const savingToneClasses = savingTone === 'positive'
    ? 'border-emerald-300/50 bg-emerald-50/70 dark:bg-emerald-900/15 dark:border-emerald-700/40'
    : savingTone === 'warning'
      ? 'border-rose-300/50 bg-rose-50/70 dark:bg-rose-900/15 dark:border-rose-700/40'
      : 'border-slate-300/60 bg-slate-50/80 dark:bg-slate-800/40 dark:border-slate-700/60';
  const savingValueClasses = savingTone === 'positive'
    ? 'text-emerald-700 dark:text-emerald-300'
    : savingTone === 'warning'
      ? 'text-rose-700 dark:text-rose-300'
      : 'text-slate-700 dark:text-slate-300';
  const savingPrefix = saving > 0 ? '+' : '';

  // Canonical Amount Engine v1:
  // If payload is already scaled by backend, UI must never scale again.
  const effectiveMultiplier = isAlreadyScaled
    ? 1
    : (currentPortion / Math.max(1, baseSrv));

  const calculateScaledTime = () => {
    const fixed = parseInt(recipe.fixedTime) || 10;
    const variable = parseInt(recipe.variableTime) || 15;
    const exponent = parseFloat(recipe.scalingModel?.exponent) || 0.7;
    if (isAlreadyScaled) return Math.round(fixed + variable);

    const baseForTime = isAlreadyScaled ? appliedPortion : baseSrv;
    if (currentPortion === baseForTime) return Math.round(fixed + variable);

    const scaleFactor = Math.pow(currentPortion / Math.max(1, baseForTime), exponent);
    return Math.round(fixed + (variable * scaleFactor));
  };

  const displayCookingTime = calculateScaledTime();

  const calculateCalories = () => {
    const rawCal = recipe.nutritionalValues?.calories || recipe.calories || recipe.kalori || "450";
    const base = parseInt(rawCal);
    if (isNaN(base)) return 450;
    if (isAlreadyScaled) return base;
    return Math.round(base * effectiveMultiplier);
  };

  const scaleIngredient = (ing) => {
    const m = effectiveMultiplier;
    if (m === 1 || isNaN(m) || !ing) return ing;

    if (typeof ing === 'string') {
      return ing.replace(/(\d+(?:[\/.,]\d+)?)/g, (match) => {
        if (match.includes('/')) {
          const [num, den] = match.split('/');
          const val = parseFloat(num) / parseFloat(den);
          return (Math.round(val * m * 10) / 10).toString().replace('.', ',');
        }
        const n = parseFloat(match.replace(',', '.'));
        if (isNaN(n)) return match;
        return (Math.round(n * m * 10) / 10).toString().replace('.', ',');
      });
    }

    if (typeof ing === 'object' && (ing.quantity || ing.miktar)) {
      // Backend'den gelen hazır ölçeklenmiş veri varsa (KitchenFactsBuilder -> IngredientScaler)
      // Orijinal metni basitçe çarpmak yanıltıcı olabilir (Örn: 300g = 10 dilim, ama ayna metin 1.5 * 4 = 6 dilim diyebilir).
      if (ing.scaledQty !== undefined) {
        return {
          ...ing,
          quantity: ing.scaledQty,
          formattedQuantity: ing.formattedQuantity || `${ing.displayQuantity || ing.scaledQty} ${ing.displayUnit || ing.unit || ''}`
        };
      }

      const n = parseFloat(ing.quantity || ing.miktar);
      if (!isNaN(n)) {
        const scaled = n * m;
        const originalText =
          ing?.display?.originalText ||
          ing.originalText ||
          `${ing.amountRaw || ''} ${ing.unitRaw || ing.unit || ''}`.trim();
        return {
          ...ing,
          quantity: scaled,
          formattedQuantity: formatQuantity(scaled, originalText, m)
        };
      }
    }

    return ing;
  };

  const showBabyInfo = !hideBabyInfo && hasBabySignal;
  const shouldShowBabyInfo = showBabyInfo && (recipe.babyInfo || recipe.babyAdjustment || recipe.babyNote);
  const recommendationReasonsRaw = Array.isArray(recipe?.recommendationExplain?.reasons)
    ? recipe.recommendationExplain.reasons.map((r) => String(r || '').trim()).filter(Boolean)
    : [];
  const normalizedMissingCount = Number.isFinite(Number(factualMissingCount))
    ? Number(factualMissingCount)
    : Number(resolvedMatch?.missingIngredients?.length || recipe?.missingIngredients?.length || 0);
  const hasMissingIngredients = normalizedMissingCount > 0;
  const contradictionPatternWhenMissing = /(eksik malzeme yok|eksik malzeme olmadan|tam uyumlu|tüm malzemeler mevcut|stok tam)/i;
  const contradictionPatternWhenFull = /(eksik malzeme[:\s]|eksik ürün|tamamlanır)/i;
  const normalizedRecommendationReasons = recommendationReasonsRaw
    .filter((reason) => {
      if (hasMissingIngredients && contradictionPatternWhenMissing.test(reason)) return false;
      if (!hasMissingIngredients && contradictionPatternWhenFull.test(reason)) return false;
      return true;
    });
  const recommendationLeadReason = hasMissingIngredients
    ? `Eksik malzeme var: ${normalizedMissingCount} kalem.`
    : 'Eksik malzeme yok, mutfak uyumu yüksek.';
  const finalRecommendationReasons = [recommendationLeadReason, ...normalizedRecommendationReasons]
    .filter((reason, idx, arr) => arr.findIndex((x) => x.toLocaleLowerCase('tr-TR') === reason.toLocaleLowerCase('tr-TR')) === idx)
    .slice(0, 5);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" onClick={(e) => e.target === e.currentTarget && handleModalClose()}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"></div>

      <div
        className={`relative ${recipe.isChefChoice ? 'bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-300 dark:border-yellow-600' : 'bg-white dark:bg-[#1a1a1a]'} 
        rounded-2xl w-full max-w-2xl max-h-[90vh] ${(legumeDetection && !legumeProcessed) ? 'overflow-hidden' : 'overflow-y-auto'} shadow-2xl animate-fade-in-up scrollbar-hide`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>

        {/* Strategic Role Badge */}
        {recipe.serveStyle && (
          <div className="absolute top-4 left-6 bg-slate-900/80 backdrop-blur-md text-white px-4 py-2 rounded-full text-xs font-black shadow-lg flex items-center gap-2 z-10 uppercase tracking-widest border border-white/10">
            <span className="material-symbols-outlined text-[16px]">
              {recipe.serveStyle === 'ana_yemek' || recipe.serveStyle === 'main' ? 'restaurant' :
                (recipe.serveStyle === 'meze' || recipe.serveStyle === 'salad' || recipe.serveStyle === 'salata') ? 'tapas' :
                  (recipe.serveStyle === 'corba' || recipe.serveStyle === 'çorba' || recipe.serveStyle === 'soup') ? 'soup_kitchen' :
                    (recipe.serveStyle === 'tatli' || recipe.serveStyle === 'tatlı' || recipe.serveStyle === 'dessert') ? 'cake' :
                      (recipe.serveStyle === 'kahvalti' || recipe.serveStyle === 'breakfast') ? 'bakery_dining' :
                        recipe.serveStyle === 'yan_yemek' ? 'bakery_dining' :
                          recipe.serveStyle === 'atıştırmalık' ? 'cookie' :
                            recipe.serveStyle === 'harc' ? 'blender' : 'format_list_bulleted'}
            </span>
            {recipe.serveStyle === 'ana_yemek' || recipe.serveStyle === 'main' ? 'Ana Yemek' :
              (recipe.serveStyle === 'meze' || recipe.serveStyle === 'salad' || recipe.serveStyle === 'salata') ? 'Meze / Salata' :
                (recipe.serveStyle === 'corba' || recipe.serveStyle === 'çorba' || recipe.serveStyle === 'soup') ? 'Çorba' :
                  (recipe.serveStyle === 'tatli' || recipe.serveStyle === 'tatlı' || recipe.serveStyle === 'dessert') ? 'Tatlı' :
                    (recipe.serveStyle === 'kahvalti' || recipe.serveStyle === 'breakfast') ? 'Kahvaltı' :
                      recipe.serveStyle === 'yan_yemek' ? 'Yan Lezzet' :
                        recipe.serveStyle === 'atıştırmalık' ? 'Atıştırmalık' :
                          recipe.serveStyle === 'harc' ? 'Sos / Harç' : 'Diğer Lezzet'}
          </div>
        )}

        {/* Match Percentage Overlay (ACE: Cookability Score Authority) */}
        {Number.isFinite(cookabilityScore) && (
          <div className={`absolute top-14 left-6 px-3 py-1.5 rounded-full border border-white/20 backdrop-blur-md z-10 flex items-center gap-1.5 shadow-xl font-black text-[10px] text-white ${isAdapted ? 'bg-indigo-600/90' :
            (cookabilityScore === 100 && factualMissingCount === 0) ? 'bg-emerald-600/90' :
              ((resolvedMatch.missingIngredients?.length > 0) || cookabilityScore === 0) ? 'bg-red-600/90' : 'bg-orange-600/90'
            }`}>
            <span className="material-symbols-outlined text-[14px]">
              {isAdapted ? 'auto_fix_high' : (cookabilityScore === 100 && factualMissingCount === 0) ? 'check_circle' : 'equalizer'}
            </span>
            {isAdapted ? `UYARLANABİLİR (%${cookabilityScore})` :
              (cookabilityScore === 100 && factualMissingCount === 0) ? 'MALZEMELER TAM' : `%${cookabilityScore} UYUMLU`}
          </div>
        )}

        {/* 🚨 ACE: Safety Warning / Premium Upsell Overlay */}
        {safetyStatus === 'UNSAFE' && (
          <div className="absolute top-24 left-6 bg-red-600/95 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-[10px] font-black shadow-xl flex items-center gap-1.5 z-10 border border-white/20 uppercase tracking-widest animate-pulse">
            <span className="material-symbols-outlined text-[14px] fill-1">warning</span>
            {userProfile?.hasAllergenPremium ? 'GÜVENSİZ TARİF' : 'ALERJEN RİSKİ'}
            {!userProfile?.hasAllergenPremium && (
              <span className="ml-1 opacity-80 border-l border-white/30 pl-1.5 flex items-center gap-1 text-[9px]">
                <span className="material-symbols-outlined text-[12px]">diamond</span>
                PREMIUM
              </span>
            )}
          </div>
        )}

        {/* Modal Header Image Area */}
        <div className="relative h-64 bg-slate-100 dark:bg-slate-800 rounded-t-2xl overflow-hidden shrink-0">
          {(() => {
            const displayUrl = recipe.image_url || recipe.imageUrl;
            if (displayUrl) {
              const src = resolveImageSrc(displayUrl);
              return (
                <img
                  src={src}
                  alt={recipe.title || recipe.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              );
            }
            return <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center text-6xl">🍽️</div>;
          })()}

          {recipe._isDefaultImage && (
            <div className="absolute bottom-4 left-6 bg-amber-500 text-white px-3 py-1 rounded-full text-[10px] font-black shadow-xl border border-white/20 z-10 animate-fade-in uppercase">
              Örnek Görsel
            </div>
          )}
          <button
            onClick={handleModalClose}
            className="absolute top-4 right-4 bg-white/90 dark:bg-gray-800/90 rounded-full p-2.5 text-slate-800 dark:text-white shadow-xl hover:scale-110 active:scale-95 transition-all z-20 border border-slate-200 dark:border-gray-700 flex items-center justify-center"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Recipe Title and Safety Section */}
        <div className="px-6 pt-6 pb-2 text-center relative">
          <div className="flex items-center justify-center flex-wrap gap-2">
            {(() => {
              const titleText = repairMojibake(processText(recipe.title || recipe.name, userProfile));
              let fontSizeClass = 'text-3xl';
              if (titleText.length > 30) fontSizeClass = 'text-xl';
              else if (titleText.length > 20) fontSizeClass = 'text-2xl';
              return <h1 className={`font-bold ${fontSizeClass} text-slate-800 dark:text-white inline-block`}>{titleText}</h1>;
            })()}
            <TermsBadge className="!static !top-auto !right-auto inline-block align-bottom translate-y-[2px]" />
            {((resolvedMatch.isAllergenSafe === false) || recipe.isAllergenSafe === false || recipe._hasAllergenCollision) && !userProfile?.hasAllergenPremium && (
              <div className="inline-flex items-center justify-center size-8 rounded-full bg-orange-500/10 text-orange-500 border border-orange-500/20 translate-y-[-2px]" title="Alerjen Kontrolü Gerekli">
                <span className="material-symbols-outlined text-[20px] font-bold">verified_user</span>
              </div>
            )}

            {/* 🔥 [DEBUG VERSION TAG] */}
            <div className="absolute top-0 right-0 p-1 opacity-20 hover:opacity-100 transition-opacity">
              <span className="text-[8px] font-mono text-slate-500">v2.5.REFRESH_UI</span>
            </div>

            {/* Smart Safety Warnings (from safetyFlags or static safetyWarnings) */}
            {(() => {
              const flags = Array.isArray(recipe.safetyFlags) ? recipe.safetyFlags : [];
              const staticWarnings = recipe.safetyWarnings || [];

              const flagMessages = {
                'kilcik_high': 'Bu yemekte çipura/levrek kullanıldı. Kılçık riski yüksek, bebek için elle kontrol edilmelidir.',
                'kilcik_very_high': 'Bu yemekte hamsi/mezgit kullanıldı. Çok ince kılçık riski var, ekstra dikkat edilmeli.',
                'kilcik_low': 'Somon kılçığı azdır ancak yine de bebek için elle kontrol edilmelidir.'
              };

              const allWarnings = repairMojibakeList(staticWarnings);

              // 🚨 CRITICAL: Brand Safety & Allergen Collisions
              if (recipe._hasAllergenCollision && Array.isArray(recipe._collidingAllergens)) {
                recipe._collidingAllergens.forEach(collision => {
                  allWarnings.push(`⚠️ DİKKAT: Dolabınızdaki "${repairMojibake(collision)}" içerikli ürün bu tarifte kullanıldığı için risk tespit edildi.`);
                });
              } else if (recipe._hasAllergenCollision) {
                allWarnings.push('⚠️ DİKKAT: Dolabınızdaki riskli bir ürün nedeniyle bu tarif alerjen uyarısı veriyor.');
              }

              flags.forEach(f => {
                if (flagMessages[f]) allWarnings.push(flagMessages[f]);
              });

              if (allWarnings.length === 0) return null;

              return (
                <div
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 align-middle ml-2 cursor-help relative group border border-orange-200 dark:border-orange-800"
                  title={allWarnings.join('\n')}
                >
                  <span className="material-symbols-outlined text-[20px] fill-1">verified_user</span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-orange-600 dark:bg-orange-700 text-white text-xs rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 text-center font-bold">
                    <div className="flex flex-col gap-2">
                      <span className="text-[10px] uppercase opacity-80 flex items-center justify-center gap-1">
                        <span className="material-symbols-outlined text-[12px]">security</span> Güvenlik Kalkanı Aktif
                      </span>
                      {allWarnings.map((w, i) => (
                        <div key={i} className={i > 0 ? 'pt-2 border-t border-white/20' : ''}>{w}</div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          {overrideInfo?.replaced && (
            <div className="mt-4 mx-auto max-w-xl rounded-2xl border border-primary/20 bg-primary/5 dark:bg-primary/10 px-4 py-3 text-left">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">swap_horiz</span>
                <div className="flex-1">
                  <div className="text-[10px] font-black uppercase tracking-widest text-primary mb-1">
                    Manuel Değişiklik
                  </div>
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-snug">
                    {overrideInfo?.removed
                      ? 'Bu slotta sistem önerisini kaldırdınız.'
                      : 'Bu slotta sistem önerisini değiştirip bu tarifi seçtiniz.'}
                  </p>
                  {overrideInfo?.previousRecipeName && (
                    <p className="mt-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                      Sistem önerisi: {overrideInfo.previousRecipeName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          {recipe.description && <p className="text-sm text-slate-600 dark:text-gray-400 mt-2">{recipe.description}</p>}
        </div>

        {contextualPrompt?.question && (
          <div className="mx-6 mb-2 rounded-2xl border border-indigo-200 bg-indigo-50/70 px-4 py-3 text-left dark:border-indigo-900/40 dark:bg-indigo-950/20">
            <p className="text-xs font-black text-slate-800 dark:text-white">{contextualPrompt.question}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {(Array.isArray(contextualPrompt.options) ? contextualPrompt.options : []).map((option) => {
                const normalizedOption = String(option);
                const selected = String(contextualPromptAnsweredValue || '') === normalizedOption;
                return (
                  <button
                    key={normalizedOption}
                    type="button"
                    onClick={() => onContextualPromptAnswer?.(normalizedOption)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      selected
                        ? 'bg-indigo-600 text-white'
                        : 'border border-indigo-200 bg-white text-indigo-700 dark:border-indigo-700 dark:bg-slate-900 dark:text-indigo-300'
                    }`}
                  >
                    {normalizedOption}
                  </button>
                );
              })}
            </div>
          </div>
        )}



        {/* Nutrition Info Bar */}
        <div className="p-6 border-b border-slate-100 dark:border-gray-700">
          <div className="flex items-center justify-center gap-6 text-center font-black">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">group</span>
              <div className="text-left dark:text-white">
                <p className="text-[10px] text-slate-500 uppercase">Porsiyon</p>
                <p className="text-sm">
                  {/* [V12.112] Fixed: Always use currentPortion (derived from userProfile or prop) to match the slider/context */}
                  {currentPortion} Kişilik
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-red-500">local_fire_department</span>
              <div className="text-left dark:text-white">
                <p className="text-[10px] text-slate-500 uppercase">Kalori</p>
                <p className="text-sm">{calculateCalories()} kcal</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-500">timer</span>
              <div className="text-left dark:text-white">
                <p className="text-[10px] text-slate-500 uppercase">Süre</p>
                <p className="text-sm">
                  {displayCookingTime} dk
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-purple-500">speed</span>
              <div className="text-left dark:text-white">
                <p className="text-[10px] text-slate-500 uppercase">Zorluk</p>
                <p className="text-sm">
                  {recipe.difficulty || 'Kolay'}
                </p>
              </div>
            </div>
          </div>


          {/* Cooking Mode Selector */}
          {validCookingModeKeys.length > 1 &&
            !['salata', 'meze', 'salad', 'appetizer', 'sos', 'harc'].includes((recipe.serveStyle || recipe.category || '').toLowerCase()) && (
              <div className="mt-6 flex flex-col items-center gap-3 animate-fade-in">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pişirme Ekipmanı Seçin</p>
                <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl w-fit">
                  {validCookingModeKeys.map(mode => (
                    <button
                      key={mode}
                      onClick={() => setActiveMode(mode)}
                      className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2
                    ${activeMode === mode ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      <span className="material-symbols-outlined text-[18px]">
                        {mode === 'pressure_cooker' ? 'cooking' :
                          mode === 'oven' ? 'oven_gen' :
                            mode === 'airfryer' ? 'airframer' :
                              mode === 'pan' ? 'skillet' : 'soup_kitchen'}
                      </span>
                      {mode === 'pressure_cooker' ? 'Düdüklü' :
                        mode === 'normal_pot' ? 'Tencere' :
                          mode === 'oven' ? 'Fırın' :
                            mode === 'airfryer' ? 'Airfryer' :
                              mode === 'pan' ? 'Tava' : mode.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Preparation Requirements (Thawing etc.) */}
        {Array.isArray(recipe.preparationRequirements) && recipe.preparationRequirements.length > 0 && (
          <div className="px-6 py-4 mx-6 mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl flex flex-col gap-3 animate-fade-in">
            <h3 className="flex items-center gap-2 text-sm font-black text-blue-800 dark:text-blue-300 uppercase tracking-wider">
              <span className="material-symbols-outlined text-[18px]">ac_unit</span> ÖN HAZIRLIK LİSTESİ
            </h3>
            <div className="space-y-2">
              {recipe.preparationRequirements.map((req, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-white/50 dark:bg-blue-900/30 rounded-xl border border-blue-100 dark:border-blue-700">
                  <span className="material-symbols-outlined text-blue-500 text-[18px] mt-0.5">
                    {req.type === 'thawing' ? 'severe_cold' : 'hourglass_top'}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-800 dark:text-gray-200">
                      {req.ingredient}: {req.method === 'refrigerator' ? 'Buzdolabında' : req.method === 'cold_water' ? 'Soğuk Suda' : req.method} çözdürülmeli
                    </p>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-[11px] font-black text-blue-600 dark:text-blue-400 uppercase flex items-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">timer</span> {req.durationMinutes} Dakika
                      </span>
                      {req.neden && (
                        <p className="text-[11px] text-slate-500 italic">“{req.neden}”</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-blue-700/70 dark:text-blue-400/70 font-bold italic text-center">
              * Bu işlemler pişirme süresine dahil değildir.
            </p>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8">
          {/* Ingredients Column */}
          <div>
            <div className="flex items-center mb-4">
              <h3 className="flex items-center gap-2 text-lg font-black dark:text-white uppercase">📋 Malzemeler</h3>
              <div className="ml-3 inline-flex items-center rounded-xl border border-slate-200 dark:border-slate-700 p-1 bg-slate-50 dark:bg-slate-900/60">
                <button
                  type="button"
                  onClick={() => setIngredientPresentationMode('traditional')}
                  className={`px-2.5 py-1.5 text-[10px] font-black rounded-lg transition-colors ${
                    ingredientPresentationMode === 'traditional'
                      ? 'bg-emerald-600 text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800'
                  }`}
                >
                  Geleneksel Mutfak
                </button>
                <button
                  type="button"
                  onClick={() => setIngredientPresentationMode('global')}
                  className={`px-2.5 py-1.5 text-[10px] font-black rounded-lg transition-colors ${
                    ingredientPresentationMode === 'global'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800'
                  }`}
                >
                  Dünya Mutfağı
                </button>
              </div>
              <button
                type="button"
                title="Bir sorun mu var?"
                onClick={openIssueModal}
                className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 bg-slate-100 text-xs font-black text-slate-700 transition hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                !
              </button>
            </div>

            {/* Alerjen etiketleri kullanıcı isteği üzerine modal üzerinden kaldırıldı */}
            {/* Ingredient List Refactor */}
            {(() => {
              const m = effectiveMultiplier;
              const sourceIngredients = recipe.scaledIngredients && recipe.scaledIngredients.length > 0
                ? recipe.scaledIngredients
                : recipe.ingredients && recipe.ingredients.length > 0
                ? recipe.ingredients
                : (recipe.malzemeler && recipe.malzemeler.length > 0
                  ? recipe.malzemeler
                  : []);
              const effectiveSourceIngredients = sourceIngredients;

              const allProcessedIngs = effectiveSourceIngredients.map((rawIng, idx) => {
                let ing = rawIng;
                const factsIng = rawIng && typeof rawIng === 'object' ? rawIng : null;

                if (rawIng && typeof rawIng === 'object') {
                  // Prefer resolved ingredient payload directly from serializer/detail route
                  ing = {
                    ...(typeof rawIng === 'object' ? rawIng : { originalText: rawIng }),
                    quantity: factsIng?.needed?.amount ?? factsIng?.canonicalNeed?.amount ?? factsIng.quantity ?? factsIng.miktar ?? factsIng.amount ?? factsIng.amountNormalized,
                    factUnit: factsIng?.needed?.unit || factsIng?.canonicalNeed?.unit || factsIng.unit || factsIng.birim || factsIng.unitRaw,
                    unit: (typeof rawIng === 'object' ? (rawIng.birim || rawIng.unit) : null) || factsIng?.needed?.unit || factsIng?.canonicalNeed?.unit || factsIng.unit || factsIng.birim || factsIng.unitRaw || "",
                    // Traditional scaling should follow structured/original ingredient text,
                    // not free-form turkmutfagi narration.
                    ...((factsIng?.display?.traditionalText || factsIng?.displayTextTrTraditional || rawIng?.display?.traditionalText || rawIng?.displayTextTrTraditional || factsIng?.display?.originalText || factsIng?.originalText || rawIng?.display?.originalText || rawIng?.originalText || rawIng?.amountRaw) ? (() => {
                      const tm = factsIng?.display?.traditionalText || factsIng?.displayTextTrTraditional || rawIng?.display?.traditionalText || rawIng?.displayTextTrTraditional || factsIng?.display?.originalText || factsIng?.originalText || rawIng?.display?.originalText || rawIng?.originalText || `${rawIng?.amountRaw || ''} ${rawIng?.unitRaw || rawIng?.unit || ''}`.trim();
                      const match = String(tm).match(/(\d+(?:[.,/]\d+)?)\s*(?:[a-zıüöçşğ]+\s+)*(su bardağı|su bardagi|adet|tane|yemek kaşığı|yemek kasigi|çay bardağı|cay bardagi|tatlı kaşığı|tatli kasigi|diş|dis|demet|paket|kavanoz|kutu)/i);
                      if (match) {
                        return {
                          originalAmount: parseFraction(match[1]),
                          originalUnit: match[2].toLowerCase().trim()
                        };
                      }
                      return {};
                    })() : {}),
                    scalingMeta: factsIng.scalingMeta || null,
                    isAvailable: (typeof rawIng === 'object' ? rawIng.isAvailable : null) ?? factsIng.isMatched ?? factsIng.isAvailable,
                    variationInfo: factsIng.variationInfo || null
                  };
                }
                else if (typeof rawIng === 'string') {
                  ing = rawIng.replace(/(\d+(?:[\/.,]\d+)?)/g, (match, p1, offset, string) => {
                    const n = parseFloat(match.replace(',', '.'));
                    if (isNaN(n) || m === 1) return match;
                    const context = string.substring(offset + match.length, offset + match.length + 20).toLowerCase();
                    const isPieceContext = ['adet', 'tane', 'diş', 'dis', 'parça', 'parca', 'demet', 'paket'].some(u => context.includes(u));
                    const scaled = n * m;
                    if (isPieceContext) return Math.round(scaled).toString().replace('.', ',');
                    return scaled.toFixed(3).replace('.', ',').replace(/,000$/, '');
                  });
                }

                const isString = typeof ing === 'string';
                const rawName = (isString ? ing : (ing.name || ing.malzeme || ing.urun_adi || ing.displayName || ing.display_name || ing.label || ing.urun || ing.ingredient?.name || '')).toLocaleLowerCase('tr-TR');

                // 🛡️ SAFE MATCHING: Find raw ingredient by name or ID instead of index
                // Search both recipe.malzemeler and recipe.ingredients
                const searchPool = [...(recipe.malzemeler || []), ...(recipe.ingredients || [])];
                const matchingRawIng = isString ? {} : searchPool.find(m =>
                  (m.dictionaryId && String(m.dictionaryId) === String(ing.dictionaryId)) ||
                  (m.id && String(m.id) === String(ing.id)) ||
                  (m.malzeme && m.malzeme.toLowerCase() === rawName) ||
                  (m.name && m.name.toLowerCase() === rawName) ||
                  (m.urun_adi && m.urun_adi.toLowerCase() === rawName)
                ) || rawIng || {};


                // ⚖️ SCALING PRINCIPLE (V12.100)
                // [V12.115] CRITICAL FIX: If backend pre-scaled the data, it placed the scaled value into `amount` or `scaledQty`.
                // Don't read the raw 1-person `quantity` if it was already scaled.
                const backendScaledQty = isAlreadyScaled ? (matchingRawIng.scaledQty || matchingRawIng.amount || rawIng.scaledQty || rawIng.amount) : undefined;
                const rawQtyFallback = parseFloat(matchingRawIng.miktar || matchingRawIng.quantity || rawIng.miktar || rawIng.quantity) || 0;
                const originalQty = backendScaledQty !== undefined ? backendScaledQty : rawQtyFallback;
                
                const originalUnit = matchingRawIng.birim || matchingRawIng.unit || rawIng.birim || rawIng.unit || "";

                 // Law: Top-level effectiveMultiplier is the absolute source of truth for scaling from baseSrv to currentPortion
                 // If backendScaledQty is used, effectiveMultiplier will be 1 (because isAlreadyScaled ensures it), so it preserves the backend's math.
                 const qTechnical = originalQty * effectiveMultiplier;
                 const n = (isString ? ing : [
                   ing.name, ing.malzeme, ing.urun_adi, ing.displayName, ing.display_name, ing.label, ing.urun,
                   ing.ingredient?.name, factsIng?.name,
                   matchingRawIng.malzeme, matchingRawIng.name, matchingRawIng.urun_adi, matchingRawIng.display_name, matchingRawIng.label
                 ].filter(x => x && typeof x === 'string' && x.trim() && x !== 'undefined').shift()) || 'Bilinmeyen Malzeme';

                 // Single-source display contract:
                 // Render exactly one backend-provided field per mode.
                 const modeKey = ingredientPresentationMode === 'global' ? 'global' : 'traditional';
                 const traditionalDisplayRaw = String(
                   factsIng?.display?.traditionalText ||
                   factsIng?.displayTextTrTraditional ||
                   rawIng?.display?.traditionalText ||
                   rawIng?.displayTextTrTraditional ||
                   ''
                 ).trim();
                 const globalDisplayRaw = String(
                   factsIng?.display?.globalText ||
                   factsIng?.displayTextGlobal ||
                   rawIng?.display?.globalText ||
                   rawIng?.displayTextGlobal ||
                   ''
                 ).trim();

                 const cleanSingleLineDisplay = (text) => {
                    const raw = String(text || '').replace(/\bundefined\b/gi, '').replace(/\bnull\b/gi, '').trim();
                    if (!raw) return '';

                   // Normalize known duplication pattern:
                   // "1 adet kabak (...) (4 adet)" -> "4 adet kabak (...)"
                   const dupAmountPattern = raw.match(/^(\d+(?:[.,]\d+)?)\s*(adet|tane|diş|dis|demet|paket)\s+(.+?)\s*\((\d+(?:[.,]\d+)?)\s*\2\)\s*$/i);
                   if (dupAmountPattern) {
                     const [, firstQty, unit, namePart, secondQty] = dupAmountPattern;
                     const finalQty = effectiveMultiplier !== 1 ? secondQty : firstQty;
                     return `${finalQty} ${unit} ${namePart}`.replace(/\s+/g, ' ').trim();
                   }

                   return raw.replace(/\s+/g, ' ').trim();
                 };

                 const traditionalAmountRaw =
                   factsIng?.amountRaw ||
                   rawIng?.amountRaw ||
                   '';
                 const traditionalUnitRaw =
                   factsIng?.unitRaw ||
                   rawIng?.unitRaw ||
                   '';
                 const traditionalFallbackRaw = String(
                   factsIng?.display?.traditionalText ||
                   factsIng?.displayTextTrTraditional ||
                   rawIng?.display?.traditionalText ||
                   rawIng?.displayTextTrTraditional ||
                   factsIng?.display?.originalText ||
                   factsIng?.originalText ||
                   rawIng?.display?.originalText ||
                   rawIng?.originalText ||
                   (String(traditionalAmountRaw).trim() || String(traditionalUnitRaw).trim()
                     ? `${String(traditionalAmountRaw).trim()} ${String(traditionalUnitRaw).trim()} ${n}`.trim()
                     : '')
                 ).trim();

                 const globalUnitRaw = String(
                   ing?.factUnit ||
                   factsIng?.unit ||
                   factsIng?.birim ||
                   rawIng?.unit ||
                   rawIng?.birim ||
                   originalUnit ||
                   ''
                 ).trim();
                 const globalDisplayQuantityRaw = String(
                   factsIng?.display?.displayQuantity ||
                   factsIng?.display?.quantity ||
                   factsIng?.displayQuantity ||
                   rawIng?.display?.displayQuantity ||
                   rawIng?.display?.quantity ||
                   rawIng?.displayQuantity ||
                   ing?.displayQuantity ||
                   ''
                 ).trim();
                 const globalFallbackRaw = String(
                   factsIng?.display?.text ||
                   rawIng?.display?.text ||
                   factsIng?.displayText ||
                   rawIng?.displayText ||
                   (globalDisplayQuantityRaw
                     ? `${globalDisplayQuantityRaw} ${n}`.trim()
                     : '') ||
                   (Number.isFinite(Number(qTechnical)) && Number(qTechnical) > 0 && globalUnitRaw
                     ? `${formatQuantity(Number(qTechnical))} ${globalUnitRaw} ${n}`.trim()
                     : '')
                 ).trim();

                 const modeDisplayRaw = modeKey === 'global'
                   ? (globalDisplayRaw || globalFallbackRaw)
                   : (traditionalDisplayRaw || traditionalFallbackRaw);
                 let displayName = cleanSingleLineDisplay(modeDisplayRaw);
                 displayName = String(displayName || '').replace(/\s+/g, ' ').trim();
                 if (!displayName) displayName = n;

                 const traditionalDetailRaw = String(
                   factsIng?.display?.originalText ||
                   rawIng?.display?.originalText ||
                   factsIng?.display?.traditionalText ||
                   factsIng?.displayTextTrTraditional ||
                   rawIng?.display?.traditionalText ||
                   rawIng?.displayTextTrTraditional ||
                   factsIng?.originalText ||
                   rawIng?.originalText ||
                   ''
                 ).trim();
                 let traditionalDetailText = cleanSingleLineDisplay(traditionalDetailRaw);
                 traditionalDetailText = String(traditionalDetailText || '').replace(/\s+/g, ' ').trim();
                 if (
                   !traditionalDetailText ||
                   traditionalDetailText.toLocaleLowerCase('tr-TR') === String(displayName || '').toLocaleLowerCase('tr-TR')
                 ) {
                   traditionalDetailText = '';
                 }

                // Law: Update the object used for rendering
                if (!isString) {
                  ing.finalDisplayName = displayName;
                  ing.traditionalDetailText = traditionalDetailText;
                  ing.quantity = qTechnical;
                }

                const availabilitySignals = [
                  ing?.isAvailable,
                  matchingRawIng?.isAvailable,
                  ing?.isPresentInRealInventory,
                  ing?.available,
                  ing?.inInventory,
                  ing?.hasStock
                ];
                const hasPositiveAvailability = availabilitySignals.some((v) => v === true || v === 1 || v === '1');
                const hasNegativeAvailability = availabilitySignals.some((v) => v === false || v === 0 || v === '0');
                // IMPORTANT: Unknown availability must stay undefined, not false.
                const isAvailable = hasPositiveAvailability ? true : (hasNegativeAvailability ? false : undefined);
                const hasAllergen = !!(ing.hasAllergen || ing._hasAllergenCollision || recipe.detectedAllergens?.some(a => (n || '').toLowerCase().includes(a.toLowerCase())));
                const policyBadge = getIngredientPolicyBadge(ing);
                const policyExplain = buildIngredientPolicyExplain(ing);

                const ingAlternativeIds = Array.isArray(ing.alternativeIds)
                  ? ing.alternativeIds.map((id) => String(id))
                  : [];
                const ingDictId = String(ing.dictionaryId || ing.id || ing.dictionary?.id || '');

                const variationInfo = (aceVariations || []).find((v) => {
                  const vOriginalId = String(v?.originalId || '');
                  const vUsedId = String(v?.usedIngredientId || v?.substituteId || '');
                  const isSameIngredient = (
                    (ingDictId && vOriginalId === ingDictId) ||
                    (ing.dictionary?.id && vOriginalId === String(ing.dictionary.id))
                  );
                  if (!isSameIngredient) return false;

                  // Guard: manual alternatives must belong to this exact ingredient line.
                  if (String(v?.reason || '').toUpperCase() === 'MANUAL_ALTERNATIVE') {
                    if (ingAlternativeIds.length === 0) return false;
                    if (vUsedId && !ingAlternativeIds.includes(vUsedId)) return false;
                  }
                  return true;
                });

                return {
                  ...ing,
                  finalDisplayName: displayName,
                  traditionalDetailText,
                  isAvailable: variationInfo ? !!variationInfo.isAvailable : isAvailable,
                  hasAllergen,
                  variationInfo,
                  rawIdx: idx,
                  _brandRisk: ing._brandRisk,
                  _brandRiskAlarms: ing._brandRiskAlarms,
                  _riskyBrand: ing._riskyBrand,
                  scalingMeta: ing.scalingMeta,
                  policyBadge,
                  policyExplain
                };
              });

              const hasExplicitAvailability = allProcessedIngs.every(i => i.isAvailable === true || i.isAvailable === false);
              // Never allow ingredient list to collapse to empty because of availability mismatch.
              // If everything is marked unavailable, still render the list so user sees what is needed.
              const availableIngsCandidate = hasExplicitAvailability
                ? allProcessedIngs.filter(i => i.isAvailable !== false)
                : allProcessedIngs;
              const availableIngs = (availableIngsCandidate.length > 0 ? availableIngsCandidate : allProcessedIngs)
                .filter(i => i.hideInDefaultList !== true);

              const normalizeLooseName = (value) => String(value || '')
                .toLocaleLowerCase('tr-TR')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/ı/g, 'i')
                .replace(/[^a-z0-9]/g, '');
              const missingNameSet = new Set(
                (resolvedMatch.missingIngredients || [])
                  .map((mIng) => {
                    if (typeof mIng === 'string') return mIng;
                    return mIng?.name || mIng?.ingredientName || mIng?.malzeme || '';
                  })
                  .map((name) => normalizeLooseName(name))
                  .filter(Boolean)
              );
              const seenMissing = new Set();
              const forceNoMissing = resolvedMatch?.missingCount === 0 && !hasExplicitAvailability;
              const missingIngs = (isFactuallyFullMatch || forceNoMissing)
                ? []
                : allProcessedIngs.filter((i) => {
                    if (i.hideInDefaultList === true) return false;
                    const nameIdx = normalizeLooseName(i.name || i.finalDisplayName || '');
                    const inMissingList = missingNameSet.has(nameIdx) || Array.from(missingNameSet).some((m) => m && (m.includes(nameIdx) || nameIdx.includes(m)));

                    if (hasExplicitAvailability) {
                      // If backend explicitly flags missing by name, honor it even when availability signal drifts.
                      if (i.isAvailable !== false && !inMissingList) return false;
                    } else if (missingNameSet.size > 0) {
                      // If explicit missing list exists, trust only that list to avoid false positives.
                      if (!inMissingList) return false;
                    } else {
                      // No explicit signal: do not aggressively mark items missing in landing/demo contexts.
                      return false;
                    }

                    if (seenMissing.has(nameIdx)) return false;
                    seenMissing.add(nameIdx);
                    return true;
                  });

              return (
                <div className="space-y-6">
                  {/* Default Ingredients */}
                  <ul className="space-y-3">
                    {availableIngs.map((ing) => (
                      <li key={ing.rawIdx} className={`flex items-start gap-3 p-2.5 rounded-xl transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50 border ${ing._brandRisk || ing.hasAllergen ? 'border-red-500 bg-red-50/30 dark:bg-red-950/20' : 'border-transparent'}`}>
                        <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${ing._brandRisk || ing.hasAllergen ? 'bg-red-600 animate-pulse' : 'bg-green-500'}`}></span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold block ${ing._brandRisk || ing.hasAllergen ? 'text-red-700 dark:text-red-400' : 'text-slate-700 dark:text-gray-300'}`}>
                              {ing.variationInfo ? (
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    {ing.variationInfo.displayText ? (
                                      <span className="text-indigo-600 dark:text-indigo-400">{ing.variationInfo.displayText}</span>
                                    ) : (
                                      <>
                                        <span className="line-through opacity-50">{ing.variationInfo.originalName}</span>
                                        <span className="material-symbols-outlined text-[12px] text-indigo-500">trending_flat</span>
                                        <span className="text-indigo-600 dark:text-indigo-400">{ing.variationInfo.substituteName}</span>
                                      </>
                                    )}
                                  </div>
                                  {!ing.isAvailable && (
                                    <span className="text-[10px] font-black text-red-500 uppercase tracking-tighter mt-0.5">
                                      MUTFAKTA YOK - LİSTEYE EKLENDİ
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col">
                                  <span>{ing.finalDisplayName}</span>
                                  {ingredientPresentationMode === 'traditional' && ing.traditionalDetailText && (
                                    <span className="mt-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400 leading-tight">
                                      {ing.traditionalDetailText}
                                    </span>
                                  )}
                                </div>
                              )}
                              {ing.familyName && !ing.finalDisplayName.toLowerCase().includes(ing.familyName.toLowerCase()) && (
                                <span className="ml-1 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-tighter">
                                  ({ing.familyName})
                                </span>
                              )}
                            </span>
                            {ing.hasAllergen && (
                              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-600 text-white rounded text-[8px] font-black uppercase tracking-tighter">
                                <span className="material-symbols-outlined text-[12px] fill-1">flag</span>
                                RİSK
                              </div>
                            )}
                            {ing.policyBadge && (
                              <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter ${ing.policyBadge.className}`}>
                                <span className="material-symbols-outlined text-[12px]">policy</span>
                                {ing.policyBadge.text}
                              </div>
                            )}
                          </div>
                          {ing._brandRisk && (
                            <div className="mt-1 flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900/50 rounded-lg shadow-sm animate-pulse w-fit">
                              <span className="material-symbols-outlined text-red-600 text-[16px] fill-1">flag</span>
                              <span className="text-[10px] font-black text-red-600 uppercase tracking-tighter">
                                MUTFAĞINIZDAKİ BU ÜRÜN RİSKLİ! ({ing._riskyBrand})
                              </span>
                            </div>
                          )}
                          {!ing._brandRisk && ing.hasAllergen && (
                            <span className="text-[9px] font-black text-orange-500 uppercase tracking-wider block mt-0.5 opacity-70">Genel Alerjen Uyarısı</span>
                          )}
                          {!ing._brandRisk && !ing.hasAllergen && ing.policyExplain && (
                            <p className="mt-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400 leading-tight">
                              {ing.policyExplain}
                            </p>
                          )}
                          {/* ⚖️ AUDIT PROOF (V8) - Restricted to Alakart Mode > 15 Portions per User Request */}
                          {ing.scalingMeta && (selectingFor === 'alakart' || selectingFor === 'private_chef') && (userPortion || userProfile?.serving_count || 1) > 15 && (
                            <button
                              onClick={() => {
                                const m = ing.scalingMeta;
                                Swal.fire({
                                  title: 'Miktar Hesabı ⚖️',
                                  html: `
                                    <div class="text-left text-xs font-mono bg-slate-100 dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                      <p class="mb-2 font-bold text-primary">--- HESAPLAMA DETAYI ---</p>
                                      <p>Kaynak: ${m.sourceQty} ${m.sourceUnit}</p>
                                      <p>Standart: ${m.canonicalQty} ${m.canonicalUnit}</p>
                                      <p>Birim Çevrim: ${m.conversionFactor}</p>
                                      
                                      <p class="mt-4 mb-2 font-bold text-primary">--- ÖLÇEKLENDİRME MANTIĞI ---</p>
                                      <p>Referans Porsiyon: ${m.referencePortion}</p>
                                      <p>Hedef Porsiyon: ${m.targetPortion}</p>
                                      <p>Faz: <span class="text-orange-500 font-bold">${m.piecewisePhase}</span></p>
                                      <p>Çarpan: ${m.multiplier}</p>
                                      
                                      <p class="mt-4 mb-2 font-bold text-primary">--- SONUÇ ---</p>
                                      <p>Ham Değer: ${m.rawScaledQty}</p>
                                      <p>Yuvarlanmış: ${ing.quantity} ${ing.unit}</p>
                                      
                                      <p class="mt-4 text-[10px] opacity-50 italic">Audit V8</p>
                                    </div>
                                  `,
                                  confirmButtonText: 'Tamam',
                                  confirmButtonColor: '#10b981'
                                });
                              }}
                              className="mt-1 flex items-center gap-1 text-[9px] font-black text-slate-400 hover:text-primary transition-colors uppercase tracking-widest"
                            >
                              <span className="material-symbols-outlined text-[12px]">scale</span> Miktar Hesabı
                            </button>
                          )}
                        </div>
                        {ing._brandRisk && (
                          <button
                            onClick={() => {
                              Swal.fire({
                                title: 'Marka Alerjen Uyarısı!',
                                html: `<div class="text-left"><p>Mutfağınızda bulunan <b>${ing._riskyBrand}</b> marka ürün şu riskleri taşıyor:</p><ul class="mt-2 list-disc pl-5"><li>${ing._brandRiskAlarms.join('</li><li>')}</li></ul><p class="mt-3 text-red-600 font-bold italic">Şefim; daha fazla güvenlik ve alerjan dostu yemek tarifleri için premium satın alabilirsiniz.</p></div>`,
                                icon: 'warning',
                                confirmButtonText: 'Anladım',
                                confirmButtonColor: '#dc2626'
                              });
                            }}
                            className="material-symbols-outlined text-red-500 hover:scale-110 transition-transform cursor-pointer"
                          >
                            info
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>

                  {/* Missing Ingredients Section */}
                  {missingIngs.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-red-100 dark:border-red-900/30">
                      <h3 className="flex items-center gap-2 text-lg font-black text-red-600 dark:text-red-400 uppercase mb-4">
                        <span className="material-symbols-outlined">shopping_cart</span>
                        Eksik Malzemeler
                      </h3>
                      <ul className="space-y-3 mb-6">
                        {missingIngs.map((ing) => {
                          const res = ingredientReservations.find(r => r.dictionaryId === (ing.dictionaryId || ing.id));
                          let reservationMsg = null;
                          if (res) {
                            const dateStr = new Date(res.targetDate).toLocaleDateString('tr-TR', { weekday: 'long' });
                            reservationMsg = `Evde var ama ${dateStr} günü için rezerve edildi.`;
                          }
                          const isReserved = !!res;

                          return (
                            <li key={ing.rawIdx} className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${isReserved
                              ? 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40'
                              : 'bg-red-50/50 dark:bg-red-950/20 border-red-100 dark:border-red-900/30'
                              }`}>
                              <span className={`material-symbols-outlined text-[20px] shrink-0 mt-0.5 ${isReserved ? 'text-amber-600' : 'text-red-600'}`}>
                                {isReserved ? 'event_busy' : 'shopping_cart'}
                              </span>
                              <div className="flex-1">
                                <div className="flex flex-col">
                                  {ing.variationInfo ? (
                                    <div className="flex items-center gap-2 mb-0.5">
                                      {ing.variationInfo.displayText ? (
                                        <span className={`text-sm font-black ${isReserved ? 'text-amber-900 dark:text-amber-200' : 'text-red-600 dark:text-red-400'}`}>
                                          {ing.variationInfo.displayText}
                                        </span>
                                      ) : (
                                        <>
                                          <span className="text-[10px] line-through opacity-40 font-bold">{ing.variationInfo.originalName}</span>
                                          <span className="material-symbols-outlined text-[10px] opacity-40">trending_flat</span>
                                          <span className={`text-sm font-black ${isReserved ? 'text-amber-900 dark:text-amber-200' : 'text-red-600 dark:text-red-400'}`}>
                                            {ing.variationInfo.substituteName} ({ing.quantity} {ing.unit})
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex flex-col">
                                      <span className={`text-sm font-black ${isReserved ? 'text-amber-900 dark:text-amber-200' : 'text-red-600 dark:text-red-400'}`}>
                                        {ing.finalDisplayName}
                                      </span>
                                      {ingredientPresentationMode === 'traditional' && ing.traditionalDetailText && (
                                        <span className={`mt-0.5 text-[10px] font-semibold leading-tight ${isReserved ? 'text-amber-700/80 dark:text-amber-300/80' : 'text-slate-500 dark:text-slate-400'}`}>
                                          {ing.traditionalDetailText}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <span className="text-[9px] font-black uppercase opacity-60 tracking-tighter">
                                    {ing.variationInfo?.reason === 'ALLERGEN' ? 'ALERJEN İKAMESİ' : 'EKSİK MALZEME'}
                                  </span>
                                </div>
                                {reservationMsg && (
                                  <p className="text-[10px] font-bold text-amber-700/70 dark:text-amber-400/70 italic mt-0.5 leading-tight">
                                    {reservationMsg}
                                  </p>
                                )}
                              </div>
                              {isReserved && (
                                <span className="material-symbols-outlined text-amber-400 text-sm opacity-50">lock</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      <button
                        onClick={handleAddToShoppingList}
                        disabled={addingToShoppingList}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl text-xs font-black shadow-lg shadow-red-600/20 transition-all active:scale-95 disabled:opacity-50"
                      >
                        <span className="material-symbols-outlined text-[20px]">add_shopping_cart</span>
                        {addingToShoppingList ? 'EKLENİYOR...' : 'EKSİKLERİ ALIŞVERİŞ LİSTESİNE EKLE'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 💰 [V21.3] MALİYET ANALİZİ (ADİSYON) */}
            {hasCriticalRescueSignal && (
              <div className="mt-8 p-4 rounded-2xl border border-amber-300/40 bg-amber-50/70 dark:bg-amber-900/15 dark:border-amber-700/40">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-amber-700 dark:text-amber-300">
                      Kritik Ürün Etkisi
                    </h4>
                    <p className="mt-1 text-sm font-bold text-amber-800 dark:text-amber-200">
                      🔥 {rescuedCount} kritik ürünü kurtarıyor
                    </p>
                    <p className="mt-1 text-[11px] font-semibold text-amber-700/80 dark:text-amber-300/80">
                      SKT öncelik skoru: {urgencyScore.toFixed(2)}
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-[20px] text-amber-600 dark:text-amber-300">local_fire_department</span>
                </div>
              </div>
            )}

            <div className={`mt-6 p-4 rounded-2xl border ${savingToneClasses}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    Dışarıda Yeme Kıyaslaması
                  </h4>
                  <p className="mt-1 text-sm font-bold text-slate-800 dark:text-slate-100">
                    Evde maliyet: {formatCurrency(adjustedDisplayedCostTotal)}
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                    Dışarıda benzeri: ~{formatCurrency(outsidePrice)}
                  </p>
                  <p className={`mt-1 text-sm font-black ${savingValueClasses}`}>
                    Tasarruf: {savingPrefix}{formatCurrency(saving)}
                  </p>
                </div>
                <span className={`material-symbols-outlined text-[20px] ${savingValueClasses}`}>
                  {savingTone === 'positive' ? 'trending_down' : (savingTone === 'warning' ? 'priority_high' : 'remove')}
                </span>
              </div>
            </div>

            {recipe.priceSnapshot && (
              <div className="mt-10 pt-8 border-t border-slate-100 dark:border-slate-800">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="flex items-center gap-2 text-lg font-black dark:text-white uppercase tracking-widest">
                    <span className="material-symbols-outlined text-primary">payments</span>
                    Maliyet Analizi
                  </h3>
                  <div className="px-3 py-1 bg-primary/10 text-primary text-[10px] font-black rounded-lg uppercase tracking-tighter border border-primary/20">
                    ADİSYON GÖRÜNÜMÜ
                  </div>
                </div>

                <div className="bg-slate-50/50 dark:bg-slate-900/50 rounded-2xl p-6 border border-slate-100 dark:border-slate-800 font-mono text-xs">
                  <div className="space-y-4">
                    {activeCostBreakdown.map((item, idx) => {
                      const scaledItemTotal = displayedCostBreakdown[idx] || 0;
                      return (
                      <div key={idx} className="flex items-baseline justify-between gap-4">
                        <span className="text-slate-500 truncate max-w-[140px] md:max-w-none">{item.name}</span>
                        <div className="flex-1 border-b border-dotted border-slate-300 dark:border-slate-700 mx-1 mb-1"></div>
                        <span className={`font-bold shrink-0 ${item.priceKnown ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400 italic'}`}>
                          {item.priceKnown
                            ? formatCurrency(scaledItemTotal)
                            : (isWaterLike(item?.name) ? formatCurrency(pseudoWaterUnitCost) : 'Fiyat Bekleniyor')
                          }
                        </span>
                      </div>
                    );})}
                  </div>

                  <div className="mt-6 pt-4 border-t-2 border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-wider">Genel Toplam</span>
                      <span className="text-lg font-black text-primary">
                        {formatCurrency(adjustedDisplayedCostTotal)}
                      </span>
                    </div>
                  </div>

                  {(unknownNonWaterCount > 0 || anomalyExcludedCount > 0) && (
                    <div className="mt-4 flex items-start gap-2 p-3 bg-amber-500/5 dark:bg-amber-500/10 rounded-xl border border-amber-500/20">
                      <span className="material-symbols-outlined text-[16px] text-amber-500 mt-0.5">info</span>
                      <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 italic leading-tight">
                        {unknownNonWaterCount > 0 ? `* Tahmini fiyat gösteriliyor: ${unknownNonWaterCount} malzeme için fiyat bekleniyor. ` : ''}
                        {anomalyExcludedCount > 0 ? `* ${anomalyExcludedCount} malzeme anomali koruması nedeniyle toplam dışı bırakılmıştır. ` : ''}
                        Genel toplam yalnızca güvenli ve fiyatı hesaplanabilen ürünler üzerinden gösterilmiştir.
                      </p>
                    </div>
                  )}

                  {(priceStatus === 'partial' || priceStatus === 'unknown') && (
                    <div className="mt-3 p-3 bg-sky-500/5 dark:bg-sky-500/10 rounded-xl border border-sky-500/20">
                      <p className="text-[10px] font-bold text-sky-700 dark:text-sky-300 leading-tight">
                        Tahmini fiyat: {formatCurrency(adjustedDisplayedCostTotal)}. {unknownNonWaterCount > 0 ? `${unknownNonWaterCount} malzeme için fiyat bekleniyor.` : 'Bazı satırlar henüz doğrulanmadı.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 🧠 KULLANICI AÇIKLAMASI */}
            {(recipe.recommendationExplain || recipe.priceTrustExplain) && (
              <div className="mt-8 grid gap-4">
                {recipe.recommendationExplain && (
                  <div className="p-4 rounded-2xl border border-sky-200/60 dark:border-sky-900/40 bg-sky-50/50 dark:bg-sky-900/10">
                    <h4 className="text-xs font-black uppercase tracking-wider text-sky-700 dark:text-sky-300 mb-2">
                      {recipe.recommendationExplain.title || 'Bu tarif neden önerildi?'}
                    </h4>
                    <ul className="space-y-1.5">
                      {finalRecommendationReasons.map((reason, idx) => (
                          <li key={`${idx}-${reason}`} className="text-[12px] font-semibold text-slate-700 dark:text-slate-300 flex items-start gap-2">
                            <span className="material-symbols-outlined text-[14px] text-sky-500 mt-0.5">check_circle</span>
                            <span>{String(reason)}</span>
                          </li>
                      ))}
                    </ul>
                  </div>
                )}

                {recipe.priceTrustExplain && (
                  <div className="p-4 rounded-2xl border border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10">
                    <h4 className="text-xs font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-2">
                      {recipe.priceTrustExplain.title || 'Bu fiyat nasıl hesaplandı?'}
                    </h4>
                    <ul className="space-y-1.5">
                      {(Array.isArray(recipe.priceTrustExplain.notes) ? recipe.priceTrustExplain.notes : [])
                        .slice(0, 5)
                        .map((note, idx) => (
                          <li key={`${idx}-${note}`} className="text-[12px] font-semibold text-slate-700 dark:text-slate-300 flex items-start gap-2">
                            <span className="material-symbols-outlined text-[14px] text-emerald-500 mt-0.5">payments</span>
                            <span>{String(note)}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Cooking Method Section */}
            {recipe.cookingModes && recipe.cookingModes[activeMode] && validCookingModeKeys.includes(activeMode) &&
              !['salata', 'meze', 'salad', 'appetizer', 'sos', 'harc'].includes((recipe.serveStyle || recipe.category || '').toLowerCase()) && (
                <div className="mt-8">
                  <div className="flex items-center mb-4">
                    <h3 className="flex items-center gap-2 text-lg font-black dark:text-white uppercase">🔥 Pişirme Şekli</h3>
                  </div>
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/10 rounded-2xl border border-orange-100 dark:border-orange-900/30">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-black text-orange-800 dark:text-orange-200 uppercase tracking-wider mb-1">
                          {activeMode === 'oven' ? 'FIRIN' :
                            activeMode === 'pan' ? 'TAVA' :
                              activeMode === 'pot' ? 'TENCERE' :
                                activeMode === 'pressure_cooker' ? 'DÜDÜKLÜ' :
                                  activeMode === 'airfryer' ? 'AIRFRYER' : activeMode}
                        </p>
                        <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                          {(() => {
                            const modeConfig = recipe.cookingModes[activeMode] || {};
                            const temp = modeConfig.tempLabel || modeConfig.temp || '';
                            const steps = temp.split('->').map(s => s.trim());

                            if (steps.length === 1) {
                              const t = steps[0].toLowerCase();
                              if (t.includes('medium-high')) return 'Orta-Yüksek Ateş';
                              if (t.includes('medium')) return 'Orta Ateş';
                              if (t.includes('high')) return 'Yüksek Ateş';
                              if (t.includes('low')) return 'Kısık Ateş';
                              return steps[0];
                            }

                            return (
                              <div className="flex flex-col gap-2 mt-2">
                                {steps.map((step, idx) => (
                                  <div key={idx} className="flex items-center gap-3">
                                    <div className="flex flex-col items-center">
                                      <div className={`size-2 rounded-full shadow-sm ${idx === 0 ? 'bg-red-500' : idx === steps.length - 1 ? 'bg-orange-300' : 'bg-orange-400'}`}></div>
                                      {idx < steps.length - 1 && <div className="w-[1.5px] h-3 bg-orange-200 dark:bg-orange-800/50 my-0.5"></div>}
                                    </div>
                                    <span className="text-[13px] leading-tight font-black opacity-90">{step}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">SÜRE</p>
                        <p className="text-lg font-black text-primary">
                          {recipe.cookingModes[activeMode].time} DK
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>

          {/* Instructions Column */}
          <div>
            {isAdapted && (
              <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-900/50 rounded-xl animate-fade-in">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400">auto_fix_high</span>
                  <div>
                    <h4 className="text-sm font-black text-indigo-900 dark:text-indigo-200 uppercase tracking-tight">Akıllı Alerjan Önerisi Aktif</h4>
                    <p className="text-[11px] text-indigo-700 dark:text-indigo-300 mt-1 leading-relaxed">
                      <span className="font-semibold">{(() => {
                        const allergies = userProfile?.alerji_listesi?.length > 0
                          ? userProfile.alerji_listesi
                          : (userProfile?.allergies?.length > 0 ? userProfile.allergies : ["Alerjen"]);
                        return repairMojibakeList(allergies).join(", ");
                      })()}</span> uyarısına göre yemeği yapabileceğiniz uygun malzemeler listelendi.
                    </p>
                  </div>
                </div>
              </div>
            )}




            {/* V12.7 Allergen Risk (RecipeModal) - Server controls premium gate */}
            {(recipe?._hasAllergenCollision) && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-2 opacity-10">
                  <span className="material-symbols-outlined text-[64px] text-red-500">warning</span>
                </div>
                <h4 className="flex items-center gap-2 text-red-700 dark:text-red-400 font-black uppercase text-xs mb-2 relative z-10">
                  <span className="material-symbols-outlined text-sm">warning</span> DİKKAT: ALERJEN RİSKİ
                </h4>
                <p className="text-sm text-red-800 dark:text-red-200 font-bold leading-relaxed relative z-10">
                  {(() => {
                    const collisions = repairMojibakeList(recipe._collidingAllergens || []);
                    if (collisions.length > 0) {
                      return (
                        <>
                          Bu tarif <span className="underline decoration-wavy decoration-red-500">{collisions.join(', ')}</span> alerjen riski barındırıyor. Lütfen porsiyonlamadan önce malzemeleri kontrol edin.
                        </>
                      );
                    }
                    return "Şefim, bu tarif profilinizdeki alerjen uyarılarıyla eşleşiyor olabilir. Lütfen porsiyonlamadan önce malzemeleri kontrol edin.";
                  })()}
                </p>
              </div>
            )}

            {/* CONTEXT WARNINGS / ADVICE */}
            {recipe.contextWarnings && recipe.contextWarnings.length > 0 && (
              <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-900/50 rounded-xl">
                <h4 className="flex items-center gap-2 text-indigo-700 dark:text-indigo-400 font-black uppercase text-xs mb-2">
                  <span className="material-symbols-outlined text-sm">psychology</span> ŞEFİN MALZEME ANALİZİ 🧠
                </h4>
                <ul className="space-y-2">
                  {recipe.contextWarnings.map((warning, i) => (
                    <li key={i} className="text-sm text-indigo-800 dark:text-indigo-200 font-bold flex items-start gap-2">
                      <span className="material-symbols-outlined text-[18px] text-blue-500 mt-0.5">info</span>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* School Lunchbox Recommendation */}
            {recipe.isLunchbox && userProfile?.children?.length > 0 && (new Date().getDay() !== 0 && new Date().getDay() !== 6) && (
              <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/50 rounded-xl">
                <h4 className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-black uppercase text-xs mb-2">
                  <span className="material-symbols-outlined text-sm">lunch_dining</span> BESLENME ÇANTASI FİKRİ 🍱
                </h4>
                <p className="text-sm text-emerald-800 dark:text-emerald-100 font-bold leading-relaxed">
                  Şefim, bu tarif hafta içi beslenme çantası için uygun!
                  <strong> {userProfile.children[0].name}</strong> için bir porsiyon kenara ayırabilirsin.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between mb-6">
              <h3 className="flex items-center gap-2 text-lg font-black dark:text-white uppercase">👨‍🍳 Yapılışı</h3>

              {/* Skill Level Tabs */}
              <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                <button
                  onClick={() => setActiveSkillLevel('acemi')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-1
                  ${activeSkillLevel === 'acemi' ? 'bg-white dark:bg-slate-700 text-green-600 dark:text-green-400 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">school</span>
                  ACEMİ
                </button>
                <button
                  onClick={() => setActiveSkillLevel('usta')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-1
                  ${activeSkillLevel === 'usta' ? 'bg-white dark:bg-slate-700 text-orange-600 dark:text-orange-400 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">restaurant_menu</span>
                  USTA
                </button>
                <button
                  onClick={() => setActiveSkillLevel('sef')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-1
                  ${activeSkillLevel === 'sef' ? 'bg-white dark:bg-slate-700 text-red-600 dark:text-red-400 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">local_dining</span>
                  ŞEF
                </button>
              </div>
            </div>
            <div className="space-y-6">
              {(() => {
                return instructions?.map((step, idx) => {
                  const islem = typeof step === 'object' ? step.islem : null;
                  const prevIslem = idx > 0 && typeof instructions[idx - 1] === 'object' ? instructions[idx - 1].islem : null;
                  const showPhaseHeader = islem && islem !== prevIslem;

                  const rawStepDetay = step;
                  const stepDetay = processSkillLevelText(rawStepDetay, userProfile);
                  const stepMode = (typeof step === 'object' && step.mode) ? step.mode : 'common';
                  let duration = typeof step === 'object' ? (step.durationMinutes || step.bekleme_sure_dk || 0) : null;

                  if (typeof step === 'object' && !duration && step.sure) {
                    const match = step.sure.match(/(\d+)/);
                    if (match) duration = parseInt(match[1]);
                  }

                  const isSafety = typeof step === 'object' && (step.isSafety || step.isSafetyInjection || step.type === 'portion_split');
                  const isCritical = stepDetay.includes('🛑') || stepDetay.includes('DURUN') || step.type === 'safety_check';
                  const subSteps = parseSubSteps(stepDetay);

                  return (
                    <div key={idx} className="flex flex-col gap-3">
                      {showPhaseHeader && (
                        <div className="flex items-center gap-3 mb-2 mt-4">
                          <div className="h-[2px] flex-1 bg-gradient-to-r from-transparent to-slate-200 dark:to-slate-700" />
                          <span className="px-4 py-1.5 bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900 rounded-lg text-[11px] font-black uppercase tracking-[0.2em] leading-none shadow-sm">
                            {islem}
                          </span>
                          <div className="h-[2px] flex-1 bg-gradient-to-l from-transparent to-slate-200 dark:to-slate-700" />
                        </div>
                      )}

                      <div className={`flex flex-col gap-3 group 
                      ${isCritical ? 'bg-red-50 dark:bg-red-900/40 p-4 rounded-2xl border-2 border-red-500 shadow-lg' : ''} 
                      ${isSafety ? 'bg-amber-50 dark:bg-amber-900/40 p-4 rounded-2xl border-2 border-amber-500 shadow-lg animate-pulse-slow' : ''}
                      ${step.hasAllergenCollision ? 'border-2 border-red-400 dark:border-red-600 bg-red-50/30' : ''}`}>
                        {/* Header: Number + Mode Icon + Duration Badge */}
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black shadow-sm ${isCritical ? 'bg-red-600 text-white' : isSafety ? 'bg-amber-600 text-white' : 'bg-primary text-white'}`}>
                              {isCritical ? '!' : ((step.isSafety || step.type === 'portion_split') && showBabyInfo) ? '👶' : step.isSafetyInjection ? '⏱️' : (step.adim || idx + 1)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1.5 text-[10px] font-black text-slate-500 dark:text-gray-400 uppercase">
                                <span className="material-symbols-outlined text-[14px]">{getModeIcon(stepMode)}</span>
                                {isSafety ? (processText(islem, userProfile) || (showBabyInfo ? 'DIKKAT: BEBEK GÜVENLİĞİ' : 'GÜVENLİK ADIMI')) : (stepMode === 'common' ? (processText(islem, userProfile) || 'Genel Hazırlık') : (
                                  stepMode === 'oven' ? 'FIRIN' :
                                    stepMode === 'pan' ? 'TAVA' :
                                      stepMode === 'pot' ? 'TENCERE' :
                                        stepMode === 'pressure_cooker' ? 'DÜDÜKLÜ' :
                                          stepMode
                                ))}
                              </div>
                              {duration > 0 && (
                                <span className="flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg text-[9px] font-black">
                                  <span className="material-symbols-outlined text-[12px]">timer</span>
                                  {duration} DK
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Shortcut UI for Linked Components */}
                        {(() => {
                          const link = step.componentLink;
                          if (!link) return null;

                          const isAvailable = availableComponents.some(c => c.componentId === link.componentId || c.componentName === link.componentName);
                          if (!isAvailable) return null;

                          const isRevealed = revealedSteps.has(idx);

                          return (
                            <div className="ml-11 mb-3">
                              <div className="bg-emerald-50 dark:bg-emerald-900/40 p-4 rounded-2xl border border-emerald-200 dark:border-emerald-800 shadow-sm flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                  <div className="size-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg">
                                    <span className="material-symbols-outlined text-sm">auto_awesome</span>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs font-black text-emerald-800 dark:text-emerald-300 uppercase tracking-widest leading-none mb-1">Kısayol Mevcut</p>
                                    <p className="text-sm font-bold text-slate-800 dark:text-white">Bu adım için elinde hazır <span className="text-emerald-600 dark:text-emerald-400">"{link.componentName || 'bileşen'}"</span> var!</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-[10px] font-black uppercase flex items-center gap-1.5 shadow-sm">
                                    <span className="material-symbols-outlined text-sm">verified</span> BU ADIMI ATLAYABİLİRSİN
                                  </div>
                                  <button
                                    onClick={() => {
                                      const next = new Set(revealedSteps);
                                      if (isRevealed) next.delete(idx);
                                      else next.add(idx);
                                      setRevealedSteps(next);
                                    }}
                                    className="px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-lg text-[10px] font-black uppercase border border-slate-200 dark:border-slate-700 hover:border-emerald-500 hover:text-emerald-600 transition-all"
                                  >
                                    {isRevealed ? 'KISAYOLA DÖN' : 'ADIMI YİNE DE GÖR'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Action Items (Sub-steps) */}
                        {(() => {
                          const link = step.componentLink;
                          const isAvailable = link && availableComponents.some(c => c.componentId === link.componentId || c.componentName === link.componentName);
                          const isRevealed = revealedSteps.has(idx);

                          if (isAvailable && !isRevealed) return null;

                          return (
                            <div className="ml-11 flex flex-col gap-2.5 animate-in fade-in slide-in-from-top-2 duration-300">
                              {subSteps.map((sub, sIdx) => (
                                <div key={sIdx} className={`flex items-start gap-2.5 p-3 rounded-xl border border-transparent transition-all ${isSafety ? 'bg-amber-100/50 dark:bg-amber-900/20' : 'bg-slate-50 dark:bg-white/5 hover:border-primary/20 hover:bg-white dark:hover:bg-white/10'}`}>
                                  <span className="mt-1 text-[11px] font-black text-primary/60 shrink-0 w-5">
                                    {subSteps.length > 1 ? `${sIdx + 1}.` : '•'}
                                  </span>
                                  <p className={`text-[13px] font-bold leading-relaxed ${isCritical ? 'text-red-800 dark:text-red-100' : isSafety ? 'text-amber-900 dark:text-amber-100' : 'text-slate-700 dark:text-gray-300'}`}>
                                    {sub}
                                  </p>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>

        {/* Bottom Tips Section - ALLERGEN vs BABY */}
        <div className="px-6 pb-6 space-y-4">


          {/* CRITICAL ALERTS (Allergies) */}
          {(() => {
            const userAllergies = userProfile?.allergies || [];
            const allergenCollision = recipe.detectedAllergens?.filter(a =>
              userAllergies.some(uA => uA.toLowerCase() === a.toLowerCase())
            ) || [];

            if (allergenCollision.length > 0) {
              return (
                <div className="p-4 bg-red-600 dark:bg-red-700 border-2 border-red-800 rounded-xl shadow-lg animate-pulse-slow">
                  <h5 className="font-black text-white text-sm mb-1 uppercase flex items-center gap-2">
                    <span className="material-symbols-outlined">report_problem</span> KRİTİK GÜVENLİK UYARISI
                  </h5>
                  <p className="text-white text-sm font-black leading-relaxed">
                    Şefim; bu yemeğe karşı alerjiniz var. lütfen tüketim yapmadan önce bu uyarıyı dikkate alın!
                  </p>
                  <p className="text-white/80 text-[10px] mt-1 font-bold italic">
                    (Tespit edilen çakışan alerjenler: {allergenCollision.join(', ')})
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* --- FOOTER REGION (Chef Note & Baby Info) --- */}
        <div className="p-6 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-8">

          {/* 1. BABY NOTE (Enhanced Box Style) */}
          {shouldShowBabyInfo && (recipe.babyInfo || recipe.babyAdjustment || recipe.babyNote) && (
            <div className="flex flex-col gap-3 animate-fade-in group">
              <div className="flex items-center gap-3 mb-1">
                <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-orange-200 dark:to-orange-900/50" />

                <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-orange-200 dark:to-orange-900/50" />
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-[2rem] border-2 border-amber-300 dark:border-amber-700 shadow-xl flex gap-5">
                <div className="size-12 rounded-2xl bg-white/50 dark:bg-amber-800/50 flex items-center justify-center shrink-0 border border-amber-200 dark:border-amber-600 shadow-inner">
                  <span className="text-2xl">👶</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-orange-500 text-[18px]">child_care</span>
                    <h4 className="text-[10px] font-black text-orange-600 dark:text-orange-400 uppercase tracking-widest">BEBEK NOTU</h4>
                  </div>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300 leading-relaxed italic">
                    {processText(recipe.babyInfo || recipe.babyAdjustment || recipe.babyNote, userProfile)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 1.5. SUGGESTED PAIRINGS (Eşlikçi Yemekler) */}
          {recipe.suggestedPairings && recipe.suggestedPairings.length > 0 && (
            <div className="flex flex-col gap-3 animate-fade-in group px-2">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="material-symbols-outlined text-amber-500 text-[18px]">auto_awesome</span>
                <h4 className="text-[11px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-[0.2em]">BU YEMEĞİN YANINA YAKIŞIR</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {recipe.suggestedPairings.map((pairing, pIdx) => (
                  <div
                    key={pIdx}
                    className="flex items-center gap-4 p-4 bg-white dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/10 shadow-sm hover:shadow-md transition-all cursor-pointer group/item"
                    onClick={() => {
                      if (onPairingClick) {
                        onPairingClick(pairing);
                      } else {
                        console.log('Suggested pairing clicked:', pairing.name);
                      }
                    }}
                  >
                    <div className="size-12 rounded-xl bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0 border border-slate-200 dark:border-white/10">
                      {pairing.image_url ? (
                        <img src={resolveImageSrc(pairing.image_url)} alt={pairing.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xl">🍽️</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-slate-800 dark:text-white truncate group-hover/item:text-primary transition-colors">{pairing.name}</p>
                      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-tighter">
                        {pairing.categoryIndex === 0 ? 'Çorba' : pairing.categoryIndex === 2 ? 'Salata' : 'Eşlikçi'}
                      </p>
                    </div>
                    <span className="material-symbols-outlined text-slate-300 dark:text-slate-700 text-[20px] group-hover/item:translate-x-1 transition-transform">chevron_right</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 2. CHEF SECRET (Redesigned Style) */}
          {(recipe.chefTip || recipe.chef_note || recipe.chefNote) && (
            <div className="flex gap-5 animate-fade-in group px-2">
              <div className="size-10 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center shrink-0 shadow-sm border border-slate-300 dark:border-slate-700">
                <span className="material-symbols-outlined text-slate-600 dark:text-slate-400 text-[20px]">person</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <h4 className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">ŞEFİN SIRRI</h4>
                  {recipe.isChefChoice && (
                    <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[9px] font-black rounded-lg uppercase border border-amber-200 dark:border-amber-800">Şefin Tercihi</span>
                  )}
                </div>
                <p className="text-sm font-bold text-slate-500 dark:text-slate-400 italic leading-relaxed font-serif">
                  "{recipe.chefTip || recipe.chef_note || recipe.chefNote}"
                </p>
              </div>
            </div>
          )}

        </div>

        {/* --- LEGUME DECISION OVERLAY --- */}
        {legumeDetection && !legumeProcessed && (
          <div className="absolute inset-0 bg-white dark:bg-[#1a1a1a] z-50 flex flex-col items-center justify-center p-8 animate-in fade-in duration-300">
            <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-4xl text-orange-500">soup_kitchen</span>
            </div>
            <h2 className="text-2xl font-black text-slate-800 dark:text-white mb-2 text-center">
              {legumeDetection.name} Hazırlığı
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-center mb-8 max-w-md">
              Bu tarifte bakliyat kullanılıyor. Malzemenizin mevcut durumu nedir?
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-3xl">
              <button
                onClick={() => setLegumeProcessed(true)}
                className="p-6 rounded-2xl border-2 border-slate-100 dark:border-slate-800 hover:border-green-500 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/10 transition-all group text-left"
              >
                <div className="mb-3 w-10 h-10 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-xl flex items-center justify-center">
                  <span className="material-symbols-outlined">water_drop</span>
                </div>
                <div className="font-bold text-slate-800 dark:text-white mb-1">Geceden Islattım</div>
                <div className="text-xs text-slate-500">Klasik yöntem, süreler normal işler.</div>
              </button>

              <button
                onClick={() => setLegumeProcessed(true)}
                className="p-6 rounded-2xl border-2 border-slate-100 dark:border-slate-800 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all group text-left"
              >
                <div className="mb-3 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-xl flex items-center justify-center">
                  <span className="material-symbols-outlined">check_circle</span>
                </div>
                <div className="font-bold text-slate-800 dark:text-white mb-1">Haşlanmış / Konserve</div>
                <div className="text-xs text-slate-500">Pişirme süresi kısalır, pratik.</div>
              </button>

              <button
                onClick={() => {
                  const detected = legumeDetection.originalKey;
                  let prepTime = 45;
                  let typeLabel = "Nohut";

                  if (detected.includes('fasulye') || detected.includes('barbunya')) {
                    prepTime = 35;
                    typeLabel = detected.includes('fasulye') ? "Kuru Fasulye" : "Barbunya";
                  } else if (detected.includes('börülce')) {
                    prepTime = 25;
                    typeLabel = "Kuru Börülce";
                  }

                  const emergencyStep = {
                    adim: 0,
                    islem: "Hızlı Bakliyat Hazırlığı",
                    mode: "pressure_cooker",
                    durationMinutes: prepTime,
                    isSafetyInjection: true,
                    detay: `Islatılmamış ${typeLabel.toLowerCase()}ı yıka, düdüklü tencereye al, üzerini 3–4 parmak soğuk suyla geç, tuz/asit ekleme; köpüğü al, kapağı kapat, basınçta ${prepTime} dk pişir, doğal basınçla dinlendir; yumuşaksa üretime hazırdır.`,
                    asistan_mesaji: "Daha önce ıslatmadığınız için bu adımı en başa ekledim şefim."
                  };
                  setExtraLegumeStep(emergencyStep);
                  setActiveMode('pressure_cooker');
                  setLegumeProcessed(true);
                }}
                className="p-6 rounded-2xl border-2 border-slate-100 dark:border-slate-800 hover:border-orange-500 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-all group text-left"
              >
                <div className="mb-3 w-10 h-10 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-xl flex items-center justify-center">
                  <span className="material-symbols-outlined">emergency_heat</span>
                </div>
                <div className="font-bold text-slate-800 dark:text-white mb-1">Hiç İşlem Yapmadım</div>
                <div className="text-xs text-slate-500">Acil durum! Düdüklü ile hızlandıralım.</div>
              </button>
            </div>
          </div>
        )}

        {issueModalOpen && (
          <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-[#101922]">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-black text-slate-900 dark:text-white">Tarifte Sorun Bildir</h3>
                <button
                  type="button"
                  onClick={() => setIssueModalOpen(false)}
                  className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Kapat
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Sorun tipi</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'ingredient_issue', label: 'Eksik / hatali malzeme' },
                      { value: 'price_issue', label: 'Yanlis fiyat' },
                      { value: 'taste_issue', label: 'Tarif & lezzet uyumsuzlugu' },
                      { value: 'other_issue', label: 'Diger' }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setIssueCategory(opt.value)}
                        className={`rounded-lg border px-3 py-2 text-left text-xs font-semibold transition ${
                          issueCategory === opt.value
                            ? 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900'
                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {issueCategory === 'ingredient_issue' && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Malzeme</div>
                    <select
                      value={issueIngredientName}
                      onChange={(e) => setIssueIngredientName(e.target.value)}
                      className="h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    >
                      <option value="">Malzeme sec</option>
                      {issueIngredients.map((item) => (
                        <option key={`issue-ingredient-${item.id}-${item.name}`} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {issueCategory === 'taste_issue' && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Lezzet alt tipi</div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {[
                        { value: 'too_salty', label: 'Fazla tuzlu' },
                        { value: 'bland', label: 'Tatsiz' },
                        { value: 'ratio_unbalanced', label: 'Oran dengesiz' }
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setIssueSubType(opt.value)}
                          className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                            issueSubType === opt.value
                              ? 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900'
                              : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Aciklama (opsiyonel)</div>
                  <textarea
                    value={issueNote}
                    onChange={(e) => setIssueNote(e.target.value)}
                    rows={3}
                    placeholder="Kisa bir not birak..."
                    className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIssueModalOpen(false)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Vazgec
                </button>
                <button
                  type="button"
                  disabled={issueSubmitting}
                  onClick={handleSubmitRecipeIssue}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                >
                  {issueSubmitting ? 'Gonderiliyor...' : 'Gonder'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fixed Footer Buttons */}
        <div className="sticky bottom-0 p-6 bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-md border-t border-slate-100 dark:border-gray-800 flex gap-4 z-30 shadow-lg">
          {!onSelect && (
            <>
              <button
                onClick={handleToggleFavorite}
                disabled={favoriteBusy}
                className={`flex-1 h-12 border rounded-xl font-black flex items-center justify-center gap-2 transition ${
                  isFavorite
                    ? 'border-rose-500 bg-rose-50 text-rose-600 dark:border-rose-400 dark:bg-rose-900/20 dark:text-rose-300'
                    : 'border-slate-300 dark:border-gray-600 dark:text-white'
                } ${favoriteBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontVariationSettings: isFavorite ? "'FILL' 1" : "'FILL' 0" }}
                >
                  favorite
                </span>
                {favoriteBusy ? 'Isleniyor...' : (isFavorite ? 'Favoride' : 'Favori')}
              </button>
              <button
                onClick={handleMarkAsUsed}
                disabled={markingAsUsed || (() => {
                  const mCount = resolvedMatch.missingCount ?? (resolvedMatch.missingIngredients?.length ?? 0);
                  const tCount = recipe.scaledIngredients?.length || recipe.malzemeler?.length || recipe.ingredients?.length || 0;
                  const isCookable = tCount > 0 && mCount === 0;
                  return !isCookable;
                })()}
                className="flex-[2] h-12 bg-primary text-white rounded-xl font-black text-lg shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:grayscale"
              >
                {markingAsUsed ? (
                  <span className="material-symbols-outlined animate-spin">sync</span>
                ) : (
                  <span className="material-symbols-outlined">check_circle</span>
                )}
                {markingAsUsed ? 'İŞLENİYOR...' : 'KULLANDIM'}
              </button>
            </>
          )}

          {/* Planning/Selection Button */}
          {onSelect && (
            <div className="flex-1 flex flex-col gap-2">
              {recipe.suggestedPairings?.length > 0 && (
                <div className="flex flex-col gap-1 mb-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <p className="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest flex items-center gap-1">
                    <span className="material-symbols-outlined text-[14px]">auto_awesome</span> AKILLI EŞLEŞME
                  </p>
                  <p className="text-[11px] font-bold text-slate-600 dark:text-slate-300">
                    Sizin için <span className="text-amber-600">{recipe.suggestedPairings.map(p => p.name).join(', ')}</span> öneriyoruz.
                  </p>
                </div>
              )}

              <div className="flex gap-2 w-full">
                <button
                  onClick={() => {
                    onSelect(isSelected ? null : recipe.id);
                    handleModalClose();
                  }}
                  className={`flex-1 h-12 rounded-xl font-black text-xs md:text-sm lg:text-lg shadow-lg flex items-center justify-center gap-2 transition-all ${isSelected ? 'bg-red-50 border-2 border-red-500 text-red-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white hover:bg-slate-200'}`}
                >
                  <span className="material-symbols-outlined">{isSelected ? 'remove_circle' : 'add_task'}</span>
                  {isSelected ? 'Seçimi Kaldır' : 'Tarifi Seç'}
                </button>

                {recipe.suggestedPairings?.length > 0 && (
                  <button
                    onClick={() => {
                      const assignments = [
                        { slot: recipe.category?.toUpperCase() || 'MAIN', recipeId: recipe.id },
                        ...recipe.suggestedPairings.map(p => ({ slot: p.category?.toUpperCase() || 'SIDE', recipeId: p.id }))
                      ];
                      onSelect(null, null, assignments);
                      handleModalClose();
                    }}
                    className="flex-[1.5] h-12 bg-primary text-white rounded-xl font-black text-sm lg:text-lg shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined">restaurant_menu</span>
                    MENÜ OLARAK EKLE
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Diet Kitchen Planning Button */}
          {onAddToPlan && (
            <div className="flex-1 flex gap-2">
              {selectingFor ? (
                <button
                  onClick={() => onAddToPlan(recipe, selectingFor)}
                  className="flex-1 h-12 bg-green-500 hover:bg-green-600 text-white rounded-xl font-black text-xs uppercase tracking-tight shadow-lg flex items-center justify-center gap-2 animate-in zoom-in-50 duration-300"
                >
                  <span className="material-symbols-outlined">add_task</span>
                  {selectingFor === 'breakfast' ? 'KAHVALTIYA EKLE' : selectingFor === 'lunch' ? 'ÖĞLEYE EKLE' : 'AKŞAMA EKLE'}
                </button>
              ) : (
                <div className="flex-1 flex gap-1">
                  <button onClick={() => onAddToPlan(recipe, 'breakfast')} className="flex-1 h-12 bg-orange-500/10 text-orange-600 border border-orange-200 rounded-xl font-black text-[10px] flex flex-col items-center justify-center gap-0.5 hover:bg-orange-500 hover:text-white transition-all">
                    <span className="material-symbols-outlined text-[18px]">wb_sunny</span> KAHVALTI
                  </button>
                  <button onClick={() => onAddToPlan(recipe, 'lunch')} className="flex-1 h-12 bg-blue-500/10 text-blue-600 border border-blue-200 rounded-xl font-black text-[10px] flex flex-col items-center justify-center gap-0.5 hover:bg-blue-500 hover:text-white transition-all">
                    <span className="material-symbols-outlined text-[18px]">lunch_dining</span> ÖĞLE
                  </button>
                  <button onClick={() => onAddToPlan(recipe, 'dinner')} className="flex-1 h-12 bg-green-500/10 text-green-600 border border-green-200 rounded-xl font-black text-[10px] flex flex-col items-center justify-center gap-0.5 hover:bg-green-500 hover:text-white transition-all">
                    <span className="material-symbols-outlined text-[18px]">restaurant</span> AKŞAM
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default RecipeModal;
